/**
 * Dashboard Album Tab Controller
 * ------------------------------
 * Host side of the guest photo album. Plan: docs/guest-photo-album-plan.md
 *
 * Three jobs, in the order a host actually does them:
 *   1. Switch the album on and get the PRINTABLE QR CARD for the tables. This
 *      step decides whether the feature works at all — an album nobody can find
 *      stays empty, so the card is treated as a designed artefact, not a bare
 *      PNG download.
 *   2. Moderate, if they chose to. Flagged photos always land here even in
 *      auto-publish mode, and there is an approve-all for the ordinary ones.
 *   3. After the event: see what is being held back, and unlock. The upsell
 *      SHOWS the photos at stake rather than counting them — what the host is
 *      buying is keeping the night, not a number.
 *
 * The album is created lazily and starts DISABLED, so opening this tab never
 * opens a public upload endpoint on someone's event.
 */
class DashboardAlbum {
    /**
     * The public marketing demo — one fictional wedding (Sarah & Michael) is
     * used across the demo form, the demo site and this album. Seeded by
     * server/scripts/_seed-demo-album.js; uploads on it are closed on purpose.
     */
    static DEMO_ALBUM_SLUG = 'sarahmichael';
    static get DEMO_ALBUM_URL() { return `/album/${DashboardAlbum.DEMO_ALBUM_SLUG}`; }

    static TRANSLATIONS = {
        en: {
            settings: 'Album settings',
            intro: 'Let guests add their own photos from their phones — no app, no account. Print the QR card for your tables.',
            enable: 'Guest photo album', enableHint: 'Off until you switch it on.',
            albumTitle: 'Album name', albumTitleHint: 'Shown to guests at the top of the album.',
            link: 'Album link', copy: 'Copy', copied: 'Copied',
            qrCard: 'Print QR cards', qrImage: 'Download QR image',
            privacy: 'Who can see it', privacyPublic: 'Anyone with the link',
            privacyGuests: 'Guests only (name must match your RSVP list)',
            privacyGuestsNoForm: 'Guests-only needs an RSVP list — not available for this album.',
            review: 'Review photos before they appear',
            reviewHint: 'Off means photos appear straight away. Anything flagged as inappropriate always waits for you either way.',
            opensOnDay: 'Open uploads on the day of the event', opensOnDayHint: 'Guests can add photos from the start of {date}. Until then the album page and your site say when it opens. You can still add your own photos now.', opensOnStatus: 'Uploads open {date}',
            statLive: 'Showing', statHidden: 'Held back', statQueued: 'Waiting for you',
            pending: 'Waiting for your review', noPending: 'Nothing waiting for review.',
            approve: 'Approve', reject: 'Delete', approveAll: 'Approve all {n}',
            approveAllConfirm: 'Approve all {n} photos waiting for review?',
            rejectConfirm: 'Delete this photo permanently? This cannot be undone.',
            livePhotos: 'In the album', noPhotos: 'No guest photos yet.',
            emptyTitle: 'Your album is ready and waiting',
            emptyBody: 'Put the QR card on your tables. Guests scan it, type their name, and their photos land here.',
            emptyPreview: 'See what your guests see', addOwn: 'Add your own photos', addOwnTip: 'Opens your album to upload your own photos, with no per-guest limit. Keep this link to yourself.', slideshow: 'Start slideshow', slideshowTip: 'Your album full screen on a TV or projector at the party. New photos come up as guests add them.',
            // ---- The OFF state's pitch. Before this, a host who had not
            // switched the album on got a title, one sentence and a switch on an
            // otherwise empty page, so the feature had eleven words to sell
            // itself. ----
            promoLead: 'Let your guests capture the moments you miss.',
            promoBody: 'One shared album for your event, filled from your guests’ phones.',
            promoFeat1: 'QR cards for your tables', promoStep1: 'Print them in one click.',
            promoFeat2: 'No app, no account', promoStep2: 'Guests scan with their camera and add photos.',
            promoFeat3: 'Yours to keep', promoStep3: 'Every photo lands here, ready to share.',
            promoDemoLink: 'See an example album',
            promoFree: 'Free on every plan: your album shows the {n} most recent photos, kept {months} months after your event. Keep every photo, at full size, for ${price}.',
            promoCta: 'Turn on the guest album',
            unlockTitle: 'Keep every photo',
            unlockBody: 'These {n} are already saved but hidden — your album shows the {visible} most recent. Unlock to keep all {total} forever, at full size, with a download of the whole album.',
            unlockBodyNoHidden: 'Keep every photo permanently at full size, and download the whole album.',
            unlock: 'Unlock Full Album — ${price}', unlocked: 'Full Album unlocked', renew: 'Keep it 1 more year — ${price}',
            andMore: '+{n} more', hiddenBadge: 'Hidden',
            termsNowTitle: 'What you have now — free',
            termsFreeShown: 'Your album shows the {n} most recent photos',
            termsFreeKept: 'Kept for {months} months after your event',
            termsFreeDeleted: 'Anything past {n} is saved but hidden — and deleted {days} days after your event if you do not unlock',
            termsPaidTitle: 'With Full Album',
            termsPaidAll: 'Every photo stays, nothing is hidden',
            termsPaidSize: 'Full size, plus a download of the whole album', termsPaidVideo: 'Videos too, up to 60 seconds each', videosProcessing: '{n} video(s) still processing; they appear here when ready.', downloadVideo: 'Download', videoPreparing: 'The video download is being prepared. Try again in a minute.',
            termsPaidKept: 'Kept {years} years, renewable',
            termsFootnote: 'One-time payment for this album. Guests can always upload — these limits only affect what you keep.',
            download: 'Download all photos', preparing: 'Preparing your album…',
            uploadsClose: 'Uploads close {date}', uploadsClosed: 'Uploads are closed.',
            expires: 'Album kept until {date}',
            loading: 'Loading…', loadError: "Couldn't load the album.", saveError: "Couldn't save that.",
            saved: 'Saved', flagged: 'Flagged',
            // Printable card
            cardHeading: 'Share your photos', cardSub: 'Scan with your phone camera',
            cardFooter: 'No app needed', cardPrint: 'Print', cardCopies: 'Cards per page'
        },
        es: {
            settings: 'Ajustes del álbum',
            intro: 'Deja que tus invitados añadan sus fotos desde el móvil — sin apps ni cuentas. Imprime la tarjeta QR para tus mesas.',
            enable: 'Álbum de fotos de invitados', enableHint: 'Apagado hasta que lo actives.',
            albumTitle: 'Nombre del álbum', albumTitleHint: 'Se muestra a los invitados en la parte superior del álbum.',
            link: 'Enlace del álbum', copy: 'Copiar', copied: 'Copiado',
            qrCard: 'Imprimir tarjetas QR', qrImage: 'Descargar imagen QR',
            privacy: 'Quién puede verlo', privacyPublic: 'Cualquiera con el enlace',
            privacyGuests: 'Solo invitados (el nombre debe coincidir con tu lista)',
            privacyGuestsNoForm: 'Solo invitados requiere una lista de invitados — no disponible para este álbum.',
            review: 'Revisar las fotos antes de que aparezcan',
            reviewHint: 'Apagado significa que las fotos aparecen al instante. Lo que se marque como inapropiado siempre esperará tu revisión.',
            opensOnDay: 'Abrir las subidas el día del evento', opensOnDayHint: 'Los invitados podrán subir fotos desde el comienzo del {date}. Hasta entonces, la página del álbum y tu sitio dicen cuándo se abre. Tú puedes subir tus fotos desde ya.', opensOnStatus: 'Las subidas se abren el {date}',
            statLive: 'Visibles', statHidden: 'Guardadas aparte', statQueued: 'Esperándote',
            pending: 'Esperando tu revisión', noPending: 'No hay nada pendiente de revisión.',
            approve: 'Aprobar', reject: 'Eliminar', approveAll: 'Aprobar las {n}',
            approveAllConfirm: '¿Aprobar las {n} fotos pendientes?',
            rejectConfirm: '¿Eliminar esta foto de forma permanente? No se puede deshacer.',
            livePhotos: 'En el álbum', noPhotos: 'Todavía no hay fotos de invitados.',
            emptyTitle: 'Tu álbum está listo y esperando',
            emptyBody: 'Pon la tarjeta QR en tus mesas. Los invitados la escanean, escriben su nombre y sus fotos llegan aquí.',
            emptyPreview: 'Ver lo que ven tus invitados', addOwn: 'Añadir tus propias fotos', addOwnTip: 'Abre tu álbum para subir tus propias fotos, sin el límite por invitado. No compartas este enlace.', slideshow: 'Iniciar presentación', slideshowTip: 'Tu álbum a pantalla completa en una tele o un proyector durante la fiesta. Las fotos nuevas aparecen en cuanto los invitados las suben.',
            promoLead: 'Deja que tus invitados capten los momentos que te pierdes.',
            promoBody: 'Un álbum compartido de tu evento, lleno de fotos desde los móviles de tus invitados.',
            promoFeat1: 'Tarjetas QR para tus mesas', promoStep1: 'Imprímelas con un clic.',
            promoFeat2: 'Sin app ni cuenta', promoStep2: 'Escanean con la cámara y suben fotos.',
            promoFeat3: 'Tuyas para siempre', promoStep3: 'Todas las fotos llegan aquí, listas para compartir.',
            promoDemoLink: 'Ver un álbum de ejemplo',
            promoFree: 'Gratis en todos los planes: tu álbum muestra las {n} fotos más recientes, guardadas {months} meses después de tu evento. Consérvalas todas, a tamaño completo, por {price}$.',
            promoCta: 'Activar el álbum de invitados',
            unlockTitle: 'Conserva todas las fotos',
            unlockBody: 'Estas {n} ya están guardadas pero ocultas — tu álbum muestra las {visible} más recientes. Desbloquea para conservar las {total} para siempre, a tamaño completo y con descarga del álbum entero.',
            unlockBodyNoHidden: 'Conserva cada foto para siempre a tamaño completo, y descarga el álbum entero.',
            unlock: 'Desbloquear álbum completo — {price}$', unlocked: 'Álbum completo desbloqueado', renew: 'Guardarlo 1 año más — {price}$',
            andMore: '+{n} más', hiddenBadge: 'Oculta',
            termsNowTitle: 'Lo que tienes ahora — gratis',
            termsFreeShown: 'Tu álbum muestra las {n} fotos más recientes',
            termsFreeKept: 'Se guarda {months} meses después de tu evento',
            termsFreeDeleted: 'Todo lo que pase de {n} se guarda pero queda oculto — y se elimina {days} días después de tu evento si no desbloqueas',
            termsPaidTitle: 'Con el álbum completo',
            termsPaidAll: 'Todas las fotos se quedan, no se oculta ninguna',
            termsPaidSize: 'Tamaño completo, más la descarga del álbum entero', termsPaidVideo: 'También vídeos, de hasta 60 segundos cada uno', videosProcessing: '{n} vídeo(s) aún en proceso; aparecerán aquí cuando estén listos.', downloadVideo: 'Descargar', videoPreparing: 'Se está preparando la descarga del vídeo. Inténtalo de nuevo en un minuto.',
            termsPaidKept: 'Se guarda {years} años, renovable',
            termsFootnote: 'Pago único por este álbum. Los invitados siempre pueden subir fotos — estos límites solo afectan a lo que conservas.',
            download: 'Descargar todas las fotos', preparing: 'Preparando tu álbum…',
            uploadsClose: 'Las subidas se cierran el {date}', uploadsClosed: 'Las subidas están cerradas.',
            expires: 'Álbum guardado hasta el {date}',
            loading: 'Cargando…', loadError: 'No pudimos cargar el álbum.', saveError: 'No pudimos guardar eso.',
            saved: 'Guardado', flagged: 'Marcada',
            cardHeading: 'Comparte tus fotos', cardSub: 'Escanea con la cámara del móvil',
            cardFooter: 'Sin apps', cardPrint: 'Imprimir', cardCopies: 'Tarjetas por página'
        },
        fr: {
            settings: "Paramètres de l'album",
            intro: "Laissez vos invités ajouter leurs photos depuis leur téléphone — sans application ni compte. Imprimez la carte QR pour vos tables.",
            enable: 'Album photo des invités', enableHint: "Désactivé tant que vous ne l'activez pas.",
            albumTitle: "Nom de l'album", albumTitleHint: "Affiché aux invités en haut de l'album.",
            link: "Lien de l'album", copy: 'Copier', copied: 'Copié',
            qrCard: 'Imprimer les cartes QR', qrImage: "Télécharger l'image QR",
            privacy: 'Qui peut le voir', privacyPublic: 'Toute personne ayant le lien',
            privacyGuests: 'Invités uniquement (le prénom doit figurer sur votre liste)',
            privacyGuestsNoForm: "Invités uniquement nécessite une liste d'invités — indisponible pour cet album.",
            review: "Valider les photos avant qu'elles apparaissent",
            reviewHint: "Désactivé signifie que les photos apparaissent aussitôt. Ce qui est signalé comme inapproprié vous attend dans tous les cas.",
            opensOnDay: 'Ouvrir les envois le jour de l\'événement', opensOnDayHint: 'Les invités pourront ajouter des photos dès le début du {date}. D\'ici là, la page de l\'album et votre site indiquent quand il ouvre. Vous pouvez déjà ajouter vos propres photos.', opensOnStatus: 'Les envois ouvrent le {date}',
            statLive: 'Affichées', statHidden: 'Mises de côté', statQueued: 'En attente',
            pending: 'En attente de validation', noPending: 'Rien à valider.',
            approve: 'Valider', reject: 'Supprimer', approveAll: 'Tout valider ({n})',
            approveAllConfirm: 'Valider les {n} photos en attente ?',
            rejectConfirm: 'Supprimer définitivement cette photo ? Cette action est irréversible.',
            livePhotos: "Dans l'album", noPhotos: "Pas encore de photos d'invités.",
            emptyTitle: 'Votre album est prêt',
            emptyBody: 'Posez la carte QR sur vos tables. Les invités la scannent, écrivent leur prénom, et leurs photos arrivent ici.',
            emptyPreview: 'Voir ce que voient vos invités', addOwn: 'Ajouter vos propres photos', addOwnTip: 'Ouvre votre album pour envoyer vos propres photos, sans limite par invité. Gardez ce lien pour vous.', slideshow: 'Lancer le diaporama', slideshowTip: 'Votre album en plein écran sur une télé ou un projecteur pendant la fête. Les nouvelles photos s’affichent dès que les invités les ajoutent.',
            promoLead: 'Laissez vos invités capturer les moments qui vous échappent.',
            promoBody: 'Un album partagé de votre événement, alimenté par les téléphones de vos invités.',
            promoFeat1: 'Cartes QR pour vos tables', promoStep1: 'Imprimez-les en un clic.',
            promoFeat2: 'Sans appli ni compte', promoStep2: 'Ils scannent avec l’appareil photo et ajoutent leurs photos.',
            promoFeat3: 'À vous pour toujours', promoStep3: 'Chaque photo arrive ici, prête à partager.',
            promoDemoLink: 'Voir un album d’exemple',
            promoFree: 'Gratuit sur toutes les formules : votre album affiche les {n} photos les plus récentes, conservées {months} mois après votre événement. Gardez-les toutes, en pleine résolution, pour {price} $.',
            promoCta: "Activer l'album des invités",
            unlockTitle: 'Gardez toutes les photos',
            unlockBody: "Ces {n} sont déjà enregistrées mais masquées — votre album affiche les {visible} plus récentes. Débloquez pour conserver les {total} pour toujours, en pleine résolution, avec le téléchargement de l'album entier.",
            unlockBodyNoHidden: "Conservez chaque photo pour toujours en pleine résolution, et téléchargez l'album entier.",
            unlock: "Débloquer l'album complet — {price} $", unlocked: 'Album complet débloqué', renew: 'Garder 1 an de plus — {price} $',
            andMore: '+{n} autres', hiddenBadge: 'Masquée',
            termsNowTitle: 'Ce que vous avez — gratuit',
            termsFreeShown: 'Votre album affiche les {n} photos les plus récentes',
            termsFreeKept: 'Conservé {months} mois après votre événement',
            termsFreeDeleted: "Au-delà de {n}, tout est enregistré mais masqué — et supprimé {days} jours après votre événement si vous ne débloquez pas",
            termsPaidTitle: "Avec l'album complet",
            termsPaidAll: "Toutes les photos restent, rien n'est masqué",
            termsPaidSize: "Pleine résolution, plus le téléchargement de l'album entier", termsPaidVideo: 'Les vidéos aussi, jusqu’à 60 secondes chacune', videosProcessing: '{n} vidéo(s) encore en traitement ; elles apparaîtront ici une fois prêtes.', downloadVideo: 'Télécharger', videoPreparing: 'Le téléchargement de la vidéo est en préparation. Réessayez dans une minute.',
            termsPaidKept: 'Conservé {years} ans, renouvelable',
            termsFootnote: "Paiement unique pour cet album. Vos invités peuvent toujours envoyer des photos — ces limites ne concernent que ce que vous gardez.",
            download: 'Télécharger toutes les photos', preparing: 'Préparation de votre album…',
            uploadsClose: 'Envois clos le {date}', uploadsClosed: 'Les envois sont clos.',
            expires: "Album conservé jusqu'au {date}",
            loading: 'Chargement…', loadError: "Impossible de charger l'album.", saveError: "Impossible d'enregistrer.",
            saved: 'Enregistré', flagged: 'Signalée',
            cardHeading: 'Partagez vos photos', cardSub: 'Scannez avec votre appareil photo',
            cardFooter: 'Aucune application', cardPrint: 'Imprimer', cardCopies: 'Cartes par page'
        },
        de: {
            settings: 'Album-Einstellungen',
            intro: 'Lasst eure Gäste eigene Fotos vom Handy hinzufügen — ohne App, ohne Konto. Druckt die QR-Karte für eure Tische.',
            enable: 'Gäste-Fotoalbum', enableHint: 'Aus, bis ihr es einschaltet.',
            albumTitle: 'Albumname', albumTitleHint: 'Wird den Gästen oben im Album angezeigt.',
            link: 'Album-Link', copy: 'Kopieren', copied: 'Kopiert',
            qrCard: 'QR-Karten drucken', qrImage: 'QR-Bild herunterladen',
            privacy: 'Wer es sehen darf', privacyPublic: 'Alle mit dem Link',
            privacyGuests: 'Nur Gäste (Name muss auf eurer Gästeliste stehen)',
            privacyGuestsNoForm: 'Nur-Gäste braucht eine Gästeliste — für dieses Album nicht verfügbar.',
            review: 'Fotos prüfen, bevor sie erscheinen',
            reviewHint: 'Aus bedeutet, Fotos erscheinen sofort. Was als unangemessen markiert wird, wartet ohnehin auf euch.',
            opensOnDay: 'Uploads am Tag der Veranstaltung öffnen', opensOnDayHint: 'Gäste können ab Beginn des {date} Fotos hinzufügen. Bis dahin zeigen die Albumseite und eure Website, wann es öffnet. Eigene Fotos könnt ihr schon jetzt hochladen.', opensOnStatus: 'Uploads öffnen am {date}',
            statLive: 'Sichtbar', statHidden: 'Zurückgehalten', statQueued: 'Wartet auf euch',
            pending: 'Wartet auf eure Prüfung', noPending: 'Nichts zu prüfen.',
            approve: 'Freigeben', reject: 'Löschen', approveAll: 'Alle {n} freigeben',
            approveAllConfirm: 'Alle {n} wartenden Fotos freigeben?',
            rejectConfirm: 'Dieses Foto endgültig löschen? Das lässt sich nicht rückgängig machen.',
            livePhotos: 'Im Album', noPhotos: 'Noch keine Gästefotos.',
            emptyTitle: 'Euer Album ist bereit',
            emptyBody: 'Stellt die QR-Karte auf eure Tische. Die Gäste scannen sie, tippen ihren Namen ein, und ihre Fotos landen hier.',
            emptyPreview: 'Ansehen, was eure Gäste sehen', addOwn: 'Eigene Fotos hinzufügen', addOwnTip: 'Öffnet euer Album zum Hochladen eigener Fotos, ohne Limit pro Gast. Diesen Link nicht weitergeben.', slideshow: 'Diashow starten', slideshowTip: 'Euer Album im Vollbild auf einem Fernseher oder Beamer bei der Feier. Neue Fotos erscheinen, sobald Gäste sie hinzufügen.',
            promoLead: 'Lasst eure Gäste die Momente festhalten, die ihr verpasst.',
            promoBody: 'Ein gemeinsames Album für euer Event, gefüllt von den Handys eurer Gäste.',
            promoFeat1: 'QR-Karten für eure Tische', promoStep1: 'Mit einem Klick ausdrucken.',
            promoFeat2: 'Keine App, kein Konto', promoStep2: 'Gäste scannen mit der Kamera und laden Fotos hoch.',
            promoFeat3: 'Für immer eures', promoStep3: 'Jedes Foto landet hier, bereit zum Teilen.',
            promoDemoLink: 'Beispielalbum ansehen',
            promoFree: 'In jedem Tarif kostenlos: Euer Album zeigt die {n} neuesten Fotos und wird {months} Monate nach eurem Event aufbewahrt. Behaltet alle in voller Größe für {price} $.',
            promoCta: 'Gäste-Album einschalten',
            unlockTitle: 'Behaltet jedes Foto',
            unlockBody: 'Diese {n} sind bereits gespeichert, aber ausgeblendet — euer Album zeigt die {visible} neuesten. Schaltet frei, um alle {total} dauerhaft in voller Auflösung zu behalten, samt Download des ganzen Albums.',
            unlockBodyNoHidden: 'Behaltet jedes Foto dauerhaft in voller Auflösung und ladet das ganze Album herunter.',
            unlock: 'Volles Album freischalten — {price} $', unlocked: 'Volles Album freigeschaltet', renew: 'Um 1 Jahr verlängern — {price} $',
            andMore: '+{n} weitere', hiddenBadge: 'Ausgeblendet',
            termsNowTitle: 'Was ihr jetzt habt — kostenlos',
            termsFreeShown: 'Euer Album zeigt die {n} neuesten Fotos',
            termsFreeKept: 'Wird {months} Monate nach eurem Event gespeichert',
            termsFreeDeleted: 'Alles über {n} hinaus wird gespeichert, aber ausgeblendet — und {days} Tage nach eurem Event gelöscht, wenn ihr nicht freischaltet',
            termsPaidTitle: 'Mit vollem Album',
            termsPaidAll: 'Jedes Foto bleibt, nichts wird ausgeblendet',
            termsPaidSize: 'Volle Auflösung, plus Download des ganzen Albums', termsPaidVideo: 'Auch Videos, bis zu 60 Sekunden pro Clip', videosProcessing: '{n} Video(s) werden noch verarbeitet und erscheinen hier, sobald sie fertig sind.', downloadVideo: 'Herunterladen', videoPreparing: 'Der Video-Download wird vorbereitet. Versuche es in einer Minute erneut.',
            termsPaidKept: '{years} Jahre gespeichert, verlängerbar',
            termsFootnote: 'Einmalzahlung für dieses Album. Gäste können immer hochladen — diese Grenzen betreffen nur, was ihr behaltet.',
            download: 'Alle Fotos herunterladen', preparing: 'Euer Album wird vorbereitet…',
            uploadsClose: 'Uploads enden am {date}', uploadsClosed: 'Uploads sind geschlossen.',
            expires: 'Album gespeichert bis {date}',
            loading: 'Wird geladen…', loadError: 'Album konnte nicht geladen werden.', saveError: 'Konnte nicht gespeichert werden.',
            saved: 'Gespeichert', flagged: 'Markiert',
            cardHeading: 'Teilt eure Fotos', cardSub: 'Mit der Handykamera scannen',
            cardFooter: 'Keine App nötig', cardPrint: 'Drucken', cardCopies: 'Karten pro Seite'
        },
        he: {
            settings: 'הגדרות האלבום',
            intro: 'אפשרו לאורחים להוסיף תמונות מהנייד — בלי אפליקציה ובלי חשבון. הדפיסו את כרטיס ה-QR לשולחנות.',
            enable: 'אלבום תמונות אורחים', enableHint: 'כבוי עד שתפעילו אותו.',
            albumTitle: 'שם האלבום', albumTitleHint: 'מוצג לאורחים בראש האלבום.',
            link: 'קישור לאלבום', copy: 'העתק', copied: 'הועתק',
            qrCard: 'הדפסת כרטיסי QR', qrImage: 'הורדת תמונת QR',
            privacy: 'מי יכול לראות', privacyPublic: 'כל מי שיש לו את הקישור',
            privacyGuests: 'אורחים בלבד (שם שתואם לרשימת המוזמנים)',
            privacyGuestsNoForm: 'אורחים בלבד דורש רשימת מוזמנים — לא זמין לאלבום הזה.',
            review: 'אישור תמונות לפני שהן מופיעות',
            reviewHint: 'כבוי פירושו שתמונות מופיעות מיד. תמונה שסומנה כבעייתית תמתין לאישורכם בכל מקרה.',
            opensOnDay: 'לפתוח את ההעלאות ביום האירוע', opensOnDayHint: 'האורחים יוכלו להוסיף תמונות מתחילת {date}. עד אז, עמוד האלבום והאתר שלכם יציגו מתי הוא נפתח. אתם יכולים להעלות תמונות משלכם כבר עכשיו.', opensOnStatus: 'ההעלאות נפתחות ב-{date}',
            statLive: 'מוצגות', statHidden: 'שמורות בצד', statQueued: 'ממתינות לכם',
            pending: 'ממתינות לאישור', noPending: 'אין תמונות שממתינות לאישור.',
            approve: 'אישור', reject: 'מחיקה', approveAll: 'אישור הכל ({n})',
            approveAllConfirm: 'לאשר את כל {n} התמונות הממתינות?',
            rejectConfirm: 'למחוק את התמונה לצמיתות? אי אפשר לבטל.',
            livePhotos: 'באלבום', noPhotos: 'עדיין אין תמונות מאורחים.',
            emptyTitle: 'האלבום מוכן ומחכה',
            emptyBody: 'הניחו את כרטיס ה-QR על השולחנות. האורחים סורקים, מקלידים את שמם, והתמונות מגיעות לכאן.',
            emptyPreview: 'לראות מה האורחים רואים', addOwn: 'הוספת התמונות שלכם', addOwnTip: 'פותח את האלבום להעלאת התמונות שלכם, בלי המגבלה לכל אורח. שמרו את הקישור הזה לעצמכם.', slideshow: 'הפעלת מצגת', slideshowTip: 'האלבום שלכם במסך מלא על טלוויזיה או מקרן במסיבה. תמונות חדשות עולות ברגע שהאורחים מוסיפים אותן.',
            promoLead: 'תנו לאורחים לתפוס את הרגעים שאתם מפספסים.',
            promoBody: 'אלבום משותף לאירוע, שמתמלא מהטלפונים של האורחים.',
            promoFeat1: 'כרטיסי QR לשולחנות', promoStep1: 'מדפיסים בלחיצה אחת.',
            promoFeat2: 'בלי אפליקציה ובלי חשבון', promoStep2: 'סורקים במצלמה ומעלים תמונות.',
            promoFeat3: 'שלכם לשמור', promoStep3: 'כל תמונה מגיעה לכאן, מוכנה לשיתוף.',
            promoDemoLink: 'לאלבום לדוגמה',
            promoFree: 'חינם בכל המסלולים: האלבום מציג את {n} התמונות האחרונות, ונשמר {months} חודשים אחרי האירוע. שמרו את כולן, באיכות מלאה, ב-{price}$.',
            promoCta: 'הפעילו את אלבום האורחים',
            unlockTitle: 'שמרו את כל התמונות',
            unlockBody: '{n} התמונות האלה כבר שמורות אך מוסתרות — האלבום מציג את {visible} האחרונות. פתחו כדי לשמור את כל {total} לתמיד, באיכות מלאה, עם הורדה של האלבום כולו.',
            unlockBodyNoHidden: 'שמרו כל תמונה לצמיתות באיכות מלאה, והורידו את האלבום כולו.',
            unlock: 'פתיחת אלבום מלא — {price}$', unlocked: 'האלבום המלא נפתח', renew: 'לשמור עוד שנה — {price}$',
            andMore: 'ועוד {n}', hiddenBadge: 'מוסתר',
            termsNowTitle: 'מה יש לכם עכשיו — בחינם',
            termsFreeShown: 'האלבום מציג את {n} התמונות האחרונות',
            termsFreeKept: 'נשמר {months} חודשים אחרי האירוע',
            termsFreeDeleted: 'כל מה שמעבר ל-{n} נשמר אך מוסתר — ונמחק {days} ימים אחרי האירוע אם לא תפתחו',
            termsPaidTitle: 'עם אלבום מלא',
            termsPaidAll: 'כל התמונות נשארות, שום דבר לא מוסתר',
            termsPaidSize: 'איכות מלאה, ובנוסף הורדה של האלבום כולו', termsPaidVideo: 'גם סרטונים, עד 60 שניות כל אחד', videosProcessing: '{n} סרטונים עדיין בעיבוד; הם יופיעו כאן כשיהיו מוכנים.', downloadVideo: 'הורדה', videoPreparing: 'הורדת הסרטון בהכנה. נסו שוב בעוד דקה.',
            termsPaidKept: 'נשמר {years} שנים, ניתן לחידוש',
            termsFootnote: 'תשלום חד-פעמי לאלבום הזה. האורחים תמיד יכולים להעלות — המגבלות נוגעות רק למה שנשמר אצלכם.',
            download: 'הורדת כל התמונות', preparing: 'מכינים את האלבום…',
            uploadsClose: 'ההעלאות נסגרות ב-{date}', uploadsClosed: 'ההעלאות סגורות.',
            expires: 'האלבום נשמר עד {date}',
            loading: 'טוען…', loadError: 'לא הצלחנו לטעון את האלבום.', saveError: 'לא הצלחנו לשמור.',
            saved: 'נשמר', flagged: 'סומן',
            cardHeading: 'שתפו את התמונות שלכם', cardSub: 'סרקו עם מצלמת הנייד',
            cardFooter: 'לא נדרשת אפליקציה', cardPrint: 'הדפסה', cardCopies: 'כרטיסים בעמוד'
        },
        pt: {
            settings: 'Definições do álbum',
            intro: 'Deixe os convidados acrescentarem as suas fotos a partir do telemóvel — sem aplicações e sem contas. Imprima o cartão QR para as suas mesas.',
            enable: 'Álbum de fotos dos convidados', enableHint: 'Desligado até o ativar.',
            albumTitle: 'Nome do álbum', albumTitleHint: 'Mostrado aos convidados no topo do álbum.',
            link: 'Ligação do álbum', copy: 'Copiar', copied: 'Copiado',
            qrCard: 'Imprimir cartões QR', qrImage: 'Descarregar imagem QR',
            privacy: 'Quem pode ver', privacyPublic: 'Qualquer pessoa com a ligação',
            privacyGuests: 'Apenas convidados (o nome tem de constar da sua lista)',
            privacyGuestsNoForm: 'Apenas convidados exige uma lista de convidados — indisponível para este álbum.',
            review: 'Rever as fotos antes de aparecerem',
            reviewHint: 'Desligado significa que as fotos aparecem de imediato. O que for assinalado como impróprio espera por si de qualquer forma.',
            opensOnDay: 'Abrir os envios no dia do evento', opensOnDayHint: 'Os convidados poderão adicionar fotos a partir do início de {date}. Até lá, a página do álbum e o seu site mostram quando ele abre. Você já pode adicionar suas próprias fotos.', opensOnStatus: 'Os envios abrem em {date}',
            statLive: 'Visíveis', statHidden: 'Guardadas à parte', statQueued: 'À sua espera',
            pending: 'À espera da sua revisão', noPending: 'Não há nada por rever.',
            approve: 'Aprovar', reject: 'Eliminar', approveAll: 'Aprovar as {n}',
            approveAllConfirm: 'Aprovar as {n} fotos pendentes?',
            rejectConfirm: 'Eliminar esta foto definitivamente? Não é possível anular.',
            livePhotos: 'No álbum', noPhotos: 'Ainda não há fotos dos convidados.',
            emptyTitle: 'O seu álbum está pronto e à espera',
            emptyBody: 'Ponha o cartão QR nas suas mesas. Os convidados digitalizam-no, escrevem o nome, e as fotos chegam aqui.',
            emptyPreview: 'Ver o que os seus convidados veem', addOwn: 'Adicionar as suas fotos', addOwnTip: 'Abre o seu álbum para enviar as suas próprias fotos, sem o limite por convidado. Não partilhe esta ligação.', slideshow: 'Iniciar apresentação', slideshowTip: 'O seu álbum em ecrã inteiro numa televisão ou num projetor durante a festa. As fotos novas aparecem assim que os convidados as adicionam.',
            promoLead: 'Deixe os seus convidados captarem os momentos que você perde.',
            promoBody: 'Um álbum partilhado do seu evento, preenchido a partir dos telemóveis dos convidados.',
            promoFeat1: 'Cartões QR para as mesas', promoStep1: 'Imprima-os com um clique.',
            promoFeat2: 'Sem app nem conta', promoStep2: 'Leem com a câmara e adicionam fotos.',
            promoFeat3: 'Seus para guardar', promoStep3: 'Cada foto chega aqui, pronta a partilhar.',
            promoDemoLink: 'Ver um álbum de exemplo',
            promoFree: 'Gratuito em todos os planos: o seu álbum mostra as {n} fotos mais recentes, guardadas {months} meses após o evento. Fique com todas, em tamanho original, por {price}$.',
            promoCta: 'Ativar o álbum dos convidados',
            unlockTitle: 'Guarde todas as fotos',
            unlockBody: 'Estas {n} já estão guardadas mas ocultas — o seu álbum mostra as {visible} mais recentes. Desbloqueie para guardar as {total} para sempre, em tamanho real, com transferência do álbum inteiro.',
            unlockBodyNoHidden: 'Guarde todas as fotos para sempre em tamanho real, e descarregue o álbum inteiro.',
            unlock: 'Desbloquear álbum completo — {price} $', unlocked: 'Álbum completo desbloqueado', renew: 'Manter por mais 1 ano — {price} $',
            andMore: '+{n} mais', hiddenBadge: 'Oculta',
            termsNowTitle: 'O que tem agora — grátis',
            termsFreeShown: 'O seu álbum mostra as {n} fotos mais recentes',
            termsFreeKept: 'Guardado {months} meses após o seu evento',
            termsFreeDeleted: 'Tudo o que passe de {n} fica guardado mas oculto — e é eliminado {days} dias após o seu evento se não desbloquear',
            termsPaidTitle: 'Com o álbum completo',
            termsPaidAll: 'Todas as fotos ficam, nada é ocultado',
            termsPaidSize: 'Tamanho real, mais a transferência do álbum inteiro', termsPaidVideo: 'Vídeos também, até 60 segundos cada', videosProcessing: '{n} vídeo(s) ainda em processamento; aparecem aqui quando estiverem prontos.', downloadVideo: 'Transferir', videoPreparing: 'A transferência do vídeo está a ser preparada. Tente de novo dentro de um minuto.',
            termsPaidKept: 'Guardado {years} anos, renovável',
            termsFootnote: 'Pagamento único por este álbum. Os convidados podem sempre enviar fotos — estes limites afetam apenas o que guarda.',
            download: 'Descarregar todas as fotos', preparing: 'A preparar o seu álbum…',
            uploadsClose: 'Os envios encerram a {date}', uploadsClosed: 'Os envios estão encerrados.',
            expires: 'Álbum guardado até {date}',
            loading: 'A carregar…', loadError: 'Não foi possível carregar o álbum.', saveError: 'Não foi possível guardar.',
            saved: 'Guardado', flagged: 'Assinalada',
            cardHeading: 'Partilhe as suas fotos', cardSub: 'Digitalize com a câmara do telemóvel',
            cardFooter: 'Sem aplicações', cardPrint: 'Imprimir', cardCopies: 'Cartões por página'
        },
        ar: {
            settings: 'إعدادات الألبوم',
            intro: 'دعوا ضيوفكم يضيفون صورهم من هواتفهم — بلا تطبيق وبلا حساب. اطبعوا بطاقة QR لطاولاتكم.',
            enable: 'ألبوم صور الضيوف', enableHint: 'مُعطّل حتى تُفعّلوه.',
            albumTitle: 'اسم الألبوم', albumTitleHint: 'يظهر للضيوف أعلى الألبوم.',
            link: 'رابط الألبوم', copy: 'نسخ', copied: 'تم النسخ',
            qrCard: 'طباعة بطاقات QR', qrImage: 'تنزيل صورة QR',
            privacy: 'من يمكنه الاطلاع', privacyPublic: 'أي شخص لديه الرابط',
            privacyGuests: 'الضيوف فقط (يجب أن يطابق الاسم قائمتكم)',
            privacyGuestsNoForm: 'خيار الضيوف فقط يحتاج قائمة ضيوف — غير متاح لهذا الألبوم.',
            review: 'مراجعة الصور قبل ظهورها',
            reviewHint: 'التعطيل يعني ظهور الصور فوراً. وما يُوسم بأنه غير لائق ينتظر مراجعتكم في كل الأحوال.',
            opensOnDay: 'فتح الرفع في يوم المناسبة', opensOnDayHint: 'سيتمكن الضيوف من إضافة الصور من بداية {date}. حتى ذلك الحين، تعرض صفحة الألبوم وموقعكم موعد فتحه. يمكنكم إضافة صوركم الآن.', opensOnStatus: 'يُفتح الرفع في {date}',
            statLive: 'ظاهرة', statHidden: 'محفوظة جانباً', statQueued: 'بانتظاركم',
            pending: 'بانتظار مراجعتكم', noPending: 'لا شيء بانتظار المراجعة.',
            approve: 'اعتماد', reject: 'حذف', approveAll: 'اعتماد الكل ({n})',
            approveAllConfirm: 'اعتماد كل الصور المنتظرة ({n})؟',
            rejectConfirm: 'حذف هذه الصورة نهائياً؟ لا يمكن التراجع.',
            livePhotos: 'في الألبوم', noPhotos: 'لا توجد صور من الضيوف بعد.',
            emptyTitle: 'ألبومكم جاهز وينتظر',
            emptyBody: 'ضعوا بطاقة QR على طاولاتكم. يمسحها الضيوف، يكتبون أسماءهم، وتصل صورهم إلى هنا.',
            emptyPreview: 'شاهدوا ما يراه ضيوفكم', addOwn: 'أضيفوا صوركم', addOwnTip: 'يفتح ألبومكم لرفع صوركم الخاصة دون حد الصور لكل ضيف. احتفظوا بهذا الرابط لأنفسكم.', slideshow: 'بدء عرض الصور', slideshowTip: 'ألبومكم بملء الشاشة على تلفاز أو جهاز عرض في الحفل. تظهر الصور الجديدة فور إضافة الضيوف لها.',
            promoLead: 'دعوا ضيوفكم يلتقطون اللحظات التي تفوتكم.',
            promoBody: 'ألبوم مشترك لمناسبتكم، يمتلئ من هواتف ضيوفكم.',
            promoFeat1: 'بطاقات QR للطاولات', promoStep1: 'اطبعوها بنقرة واحدة.',
            promoFeat2: 'بلا تطبيق ولا حساب', promoStep2: 'يمسحون بالكاميرا ويضيفون الصور.',
            promoFeat3: 'لكم للاحتفاظ بها', promoStep3: 'كل صورة تصل إلى هنا، جاهزة للمشاركة.',
            promoDemoLink: 'شاهدوا ألبوماً للمثال',
            promoFree: 'مجاني في كل الباقات: يعرض ألبومكم أحدث {n} صورة، ويُحفظ {months} شهراً بعد مناسبتكم. احتفظوا بكل الصور بالحجم الكامل مقابل {price}$.',
            promoCta: 'تشغيل ألبوم الضيوف',
            unlockTitle: 'احتفظوا بكل صورة',
            unlockBody: 'هذه الـ{n} محفوظة بالفعل لكنها مخفية — ألبومكم يعرض أحدث {visible}. افتحوا الألبوم للاحتفاظ بالـ{total} كلها إلى الأبد، بالحجم الكامل، مع تنزيل الألبوم بأكمله.',
            unlockBodyNoHidden: 'احتفظوا بكل صورة إلى الأبد بالحجم الكامل، ونزّلوا الألبوم بأكمله.',
            unlock: 'فتح الألبوم الكامل — {price} $', unlocked: 'تم فتح الألبوم الكامل', renew: 'الاحتفاظ به لسنة إضافية — {price} $',
            andMore: '+{n} أخرى', hiddenBadge: 'مخفية',
            termsNowTitle: 'ما لديكم الآن — مجاناً',
            termsFreeShown: 'ألبومكم يعرض أحدث {n} صورة',
            termsFreeKept: 'محفوظ {months} شهراً بعد مناسبتكم',
            termsFreeDeleted: 'كل ما يتجاوز {n} يُحفظ لكنه يبقى مخفياً — ويُحذف بعد {days} يوماً من مناسبتكم إن لم تفتحوا الألبوم',
            termsPaidTitle: 'مع الألبوم الكامل',
            termsPaidAll: 'كل صورة تبقى، ولا شيء يُخفى',
            termsPaidSize: 'الحجم الكامل، بالإضافة إلى تنزيل الألبوم بأكمله', termsPaidVideo: 'والفيديوهات أيضًا، حتى 60 ثانية لكل منها', videosProcessing: '{n} فيديو قيد المعالجة؛ ستظهر هنا عند جاهزيتها.', downloadVideo: 'تنزيل', videoPreparing: 'يجري تجهيز تنزيل الفيديو. حاولوا مرة أخرى بعد دقيقة.',
            termsPaidKept: 'محفوظ {years} سنتين، قابل للتجديد',
            termsFootnote: 'دفعة واحدة لهذا الألبوم. يستطيع الضيوف الرفع دائماً — هذه الحدود تخص ما تحتفظون به فقط.',
            download: 'تنزيل كل الصور', preparing: 'نجهّز ألبومكم…',
            uploadsClose: 'يُغلق الرفع في {date}', uploadsClosed: 'تم إغلاق الرفع.',
            expires: 'الألبوم محفوظ حتى {date}',
            loading: 'جارٍ التحميل…', loadError: 'تعذّر تحميل الألبوم.', saveError: 'تعذّر الحفظ.',
            saved: 'تم الحفظ', flagged: 'موسومة',
            cardHeading: 'شاركوا صوركم', cardSub: 'امسحوا بكاميرا الهاتف',
            cardFooter: 'لا حاجة لتطبيق', cardPrint: 'طباعة', cardCopies: 'بطاقات في الصفحة'
        }
    };

    constructor() {
        this.formId = null;
        this.albumId = null;   // set only for standalone albums, opened by id
        this.album = null;
        this.limits = null;
        this.lang = 'en';
        this.livePhotos = [];
    }

    t(key, vars) {
        const pack = DashboardAlbum.TRANSLATIONS[this.lang] || DashboardAlbum.TRANSLATIONS.en;
        let s = pack[key] || DashboardAlbum.TRANSLATIONS.en[key] || key;
        if (vars) Object.keys(vars).forEach((k) => { s = s.split('{' + k + '}').join(vars[k]); });
        return s;
    }

    _detectLang() {
        const html = document.documentElement.lang || '';
        this.lang = DashboardAlbum.TRANSLATIONS[html] ? html : 'en';
    }

    get isRtl() { return document.documentElement.dir === 'rtl'; }

    async _api(path, options) {
        const token = await firebase.auth().currentUser?.getIdToken();
        if (!token) throw new Error('Not signed in');
        const opts = Object.assign({ headers: {} }, options || {});
        opts.headers = Object.assign(
            { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            opts.headers
        );
        const res = await fetch('/api/albums' + path, opts);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) throw new Error(data.error || 'Request failed');
        return data;
    }

    get isDemo() { return !!window.isDemoMode; }

    /**
     * The demo dashboard has no signed-in user, so every /api/albums call is a
     * 401 and this tab used to end at "Not signed in" — the one tab in the demo
     * that showed an error instead of the feature. So demo mode renders a
     * SWITCHED-ON album built from the public demo album's real photos (the
     * same Sarah & Michael album the marketing page's QR points at), with saves
     * refused the way every other demo write is.
     */
    async _initDemo(formId) {
        this.formId = formId;
        this.albumId = null;
        this.limits = {
            FREE_VISIBLE: 30, UPLOAD_WINDOW_DAYS: 90, FREE_RETENTION_MONTHS: 12,
            PAID_RETENTION_YEARS: 2, UNLOCK_PRICE_USD: 49, MAX_PER_GUEST: 200
        };

        let photos = [];
        try {
            const res = await fetch(`/api/albums/public/${DashboardAlbum.DEMO_ALBUM_SLUG}`);
            const data = await res.json();
            if (res.ok && data.success && Array.isArray(data.photos)) photos = data.photos;
        } catch (err) {
            console.warn('[album] demo sample unavailable:', err && err.message);
        }

        const now = new Date();
        const plus = (d) => new Date(now.getTime() + d * 86400000).toISOString();
        const live = photos.length;
        this.album = {
            id: 'demo-guest-album', isDraft: false,
            slug: DashboardAlbum.DEMO_ALBUM_SLUG,
            title: document.getElementById('dashboard-title')?.dataset?.eventTitle || 'Sarah & Mike’s Wedding',
            formId, websiteId: null,
            enabled: true, privacy: 'public', reviewMode: false, isPaid: false,
            eventDate: null, uploadsCloseAt: plus(90), expiresAt: plus(365),
            uploadsOpen: true,
            counts: { live, queued: 0, total: live, hidden: Math.max(0, live - 30) },
            unlockPriceUSD: 49, freeVisible: 30
        };

        this.render();
        const loading = document.getElementById('albumLoadingState');
        const root = document.getElementById('albumMainContent');
        if (loading) loading.style.display = 'none';
        if (root) root.style.display = '';

        // render() kicked off _loadPhotos()/_startLiveFeed(), both of which are
        // no-ops in demo mode; paint the sample straight into the grids instead.
        this.livePhotos = photos.map((p, i) => ({
            id: p.id || `demo-${i}`, status: 'live',
            thumbUrl: p.thumbUrl, displayUrl: p.displayUrl,
            uploaderName: p.uploaderName || null, flagReason: null,
            uploadedAt: p.uploadedAt || null
        }));
        this._renderLiveGrid(this.livePhotos);
        const pendingEl = document.getElementById('albumPendingGrid');
        if (pendingEl) pendingEl.innerHTML = `<div class="text-muted small">${this._esc(this.t('noPending'))}</div>`;
        this._renderEmptyState(0);
        this._renderLockedStrip();
        this._wirePhotoActions();
        this._loadQrPreview();
    }

    async init(formId) {
        this._detectLang();
        if (this.isDemo) return this._initDemo(formId);
        this.formId = formId;
        const root = document.getElementById('albumMainContent');
        const loading = document.getElementById('albumLoadingState');
        if (loading) loading.style.display = '';
        if (root) root.style.display = 'none';

        try {
            const data = await this._api('/for?formId=' + encodeURIComponent(formId));
            this.album = data.album;
            this.limits = data.limits;
            this.render();
            if (loading) loading.style.display = 'none';
            if (root) root.style.display = '';
            if ((new URLSearchParams(location.search).get('album') === 'unlocked' && !this.album.isPaid)
                || new URLSearchParams(location.search).get('album') === 'renewed') {
                // The webhook may land a moment after the redirect; re-check once.
                setTimeout(() => this.refresh().then(() => this.render()), 2500);
            }
        } catch (err) {
            if (loading) loading.innerHTML = `<p class="text-danger mb-0">${this._esc(err.message || this.t('loadError'))}</p>`;
        }
    }

    /**
     * Open a STANDALONE album by id — the album-manage page has no formId to
     * look one up with. Everything after the fetch is identical to init(),
     * which is the whole reason that page reuses this controller instead of
     * growing a second copy of the tab.
     */
    async initById(albumId) {
        this._detectLang();
        this.albumId = albumId;
        this.formId = null;
        const root = document.getElementById('albumMainContent');
        const loading = document.getElementById('albumLoadingState');
        if (loading) loading.style.display = '';
        if (root) root.style.display = 'none';

        try {
            const data = await this._api('/' + encodeURIComponent(albumId));
            this.album = data.album;
            this.limits = data.limits;
            this.render();
            if (loading) loading.style.display = 'none';
            if (root) root.style.display = '';

            const heading = document.getElementById('albumHeading');
            if (heading && this.album.title) heading.textContent = this.album.title;

            if ((new URLSearchParams(location.search).get('album') === 'unlocked' && !this.album.isPaid)
                || new URLSearchParams(location.search).get('album') === 'renewed') {
                // The webhook may land a moment after the Stripe redirect.
                setTimeout(() => this.refresh().then(() => this.render()), 2500);
            }
        } catch (err) {
            if (loading) loading.innerHTML = `<p class="text-danger mb-0">${this._esc(err.message || this.t('loadError'))}</p>`;
        }
    }

    async refresh() {
        // Standalone albums are fetched by id; event albums by formId.
        if (this.albumId) {
            const data = await this._api('/' + encodeURIComponent(this.albumId));
            this.album = data.album;
            return;
        }
        if (!this.formId) return;
        const data = await this._api('/for?formId=' + encodeURIComponent(this.formId));
        this.album = data.album;
    }

    /**
     * Live photo feed.
     *
     * A host watching their own reception should see photos appear as guests
     * send them — not after a refresh, and not after switching tabs. Firestore
     * rules grant the owner READ on their album and its photos (writes stay
     * server-only), so this is a real listener rather than a poll: only changed
     * docs are billed, and a forgotten tab costs nothing while nothing changes.
     *
     * Deliberately incremental — it patches the grids in place instead of
     * re-rendering the tab, so a host mid-way through typing an album name or
     * scrolled into the queue is never yanked back to the top.
     */
    _startLiveFeed() {
        this._stopLiveFeed();
        if (this.isDemo) return; // no signed-in user, so no Firestore listener
        if (!this.album || !this.album.enabled) return;
        if (typeof firebase === 'undefined' || !firebase.firestore) return;

        try {
            this._unsub = firebase.firestore()
                .collection('albums').doc(this.album.id)
                .collection('photos')
                .orderBy('uploadedAt', 'desc')
                .limit(200)
                .onSnapshot((snap) => {
                    const all = snap.docs.map((d) => {
                        const p = d.data() || {};
                        const ts = p.uploadedAt && p.uploadedAt.toDate ? p.uploadedAt.toDate() : null;
                        return {
                            id: d.id,
                            status: p.status,
                            thumbUrl: p.thumbUrl,
                            displayUrl: p.displayUrl,
                            uploaderName: p.uploaderName || null,
                            flagReason: p.flagReason || null,
                            uploadedAt: ts ? ts.toISOString() : null
                        };
                    });
                    this._applyLive(all);
                }, (err) => {
                    // Rules not deployed yet, offline, whatever — the tab still
                    // works off the REST load, it just stops being live.
                    console.warn('[album] live feed unavailable:', err && err.message);
                    this._stopLiveFeed();
                });
        } catch (err) {
            console.warn('[album] could not start live feed:', err && err.message);
        }
    }

    _stopLiveFeed() {
        if (this._unsub) { try { this._unsub(); } catch (_) {} this._unsub = null; }
    }

    /** Repaint from a snapshot without disturbing what the host is doing. */
    _applyLive(all) {
        const live = all.filter((p) => p.status === 'live');
        const queued = all.filter((p) => p.status === 'queued');
        const flagged = all.filter((p) => p.status === 'flagged');

        // Keep the counts the header shows in step with what just arrived.
        this.album.counts.live = live.length;
        this.album.counts.queued = queued.length + flagged.length;
        this.album.counts.total = all.length;
        this.album.counts.hidden = this.album.isPaid
            ? 0 : Math.max(0, live.length - this.album.freeVisible);
        this._paintStats();

        this.livePhotos = live;
        const pending = [...flagged, ...queued];

        const pendingEl = document.getElementById('albumPendingGrid');
        if (pendingEl) {
            pendingEl.innerHTML = pending.length
                ? this._photoGrid(pending, true)
                : `<div class="text-muted small">${this._esc(this.t('noPending'))}</div>`;
        }
        this._renderLiveGrid(live);
        const pendingCard = document.getElementById('albumPendingCard');
        if (pendingCard) pendingCard.style.display = (pending.length || all.length) ? '' : 'none';

        this._renderApproveAll(queued);
        this._renderLockedStrip();
        this._renderEmptyState(pending.length);
        this._wirePhotoActions();
        this._repaintUnlock();
    }

    /** Update just the three stat numbers, in place. */
    _paintStats() {
        const row = document.getElementById('albumStatsRow');
        if (!row) return;
        const nums = row.querySelectorAll('[data-stat]');
        nums.forEach((el) => {
            const key = el.dataset.stat;
            el.textContent = this.album.counts[key] != null ? this.album.counts[key] : 0;
        });
    }

    /** The unlock card's copy depends on the hidden count, so redraw just it. */
    _repaintUnlock() {
        const slot = document.getElementById('albumUnlockSlot');
        if (!slot) return;
        slot.innerHTML = this._unlockCard(this.album);
        document.getElementById('albumUnlockBtn')?.addEventListener('click', (e) => this._unlock(e.currentTarget));
        document.getElementById('albumDownloadBtn')?.addEventListener('click', (e) => this._download(e.currentTarget));
        this._renderLockedStrip();
    }

    _esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /** The event day as the host reads it. eventDate is a plain 'YYYY-MM-DD'. */
    _eventDay(eventDate) {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(eventDate || ''));
        if (!m) return '';
        try {
            return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(this.lang === 'he' ? 'he-IL' : (this.lang || undefined),
                { month: 'long', day: 'numeric' });
        } catch { return ''; }
    }

    /** This browser's UTC offset on the event day itself, so a DST change between now and then is honoured. */
    _eventDayOffset() {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(this.album?.eventDate || ''));
        return (m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date()).getTimezoneOffset();
    }

    _date(iso) {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString(this.lang === 'he' ? 'he-IL' : (this.lang || undefined),
                { year: 'numeric', month: 'short', day: 'numeric' });
        } catch { return ''; }
    }

    get albumUrl() { return `${location.origin}/album/${this.album.slug}`; }
    /** What we print on the card — no scheme, easier to read and to retype. */
    get albumUrlPretty() { return this.albumUrl.replace(/^https?:\/\//, ''); }

    // -----------------------------------------------------------------------
    // render
    // -----------------------------------------------------------------------

    render() {
        const root = document.getElementById('albumMainContent');
        if (!root || !this.album) return;
        const a = this.album;

        root.innerHTML = `
            ${this._styles()}
            <div class="card mb-3">
                <div class="card-body">
                    <!-- One control group: the heading IS the switch's label
                         (task #765). It used to read "Guest photos" on the
                         left and "Guest photo album" beside the switch on the
                         right — the same thing named twice, a row apart.
                         The switch follows the heading directly: with
                         justify-between it landed a screen-width away from
                         its own label on desktop. mt-0 drops Bootstrap's
                         .25em top margin, and the wrapper is a centred flex
                         box because .form-check's 1.5rem min-height otherwise
                         leaves the 1em switch pinned to its top edge. -->
                    <div class="d-flex align-items-center gap-3 mb-1">
                        <h5 class="mb-0"><label for="albumEnabled" class="mb-0" style="cursor:pointer;">${this._esc(this.t('enable'))}</label></h5>
                        <div class="form-check form-switch m-0 ps-0 d-flex align-items-center" style="flex:0 0 auto;">
                            <input class="form-check-input ms-0 mt-0" type="checkbox" role="switch" id="albumEnabled" aria-label="${this._esc(this.t('enable'))}" ${a.enabled ? 'checked' : ''}>
                        </div>
                    </div>
                    <p class="text-muted mb-0" style="font-size:0.9rem;">${this._esc(this.t('intro'))}</p>
                </div>
            </div>

            ${a.enabled ? '' : this._promoCard(a)}

            <div id="albumSettingsWrap" style="${a.enabled ? '' : 'display:none;'}">
                ${a.slug ? this._shareCard(a) : ''}
                ${this._settingsCard(a)}
                ${this._statsRow(a)}
                <div id="albumUnlockSlot">${this._unlockCard(a)}</div>
                <div id="albumEmptySlot"></div>
                <div class="card mb-3" id="albumPendingCard">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <h6 class="mb-0">${this._esc(this.t('pending'))}</h6>
                            <div id="albumApproveAllSlot"></div>
                        </div>
                        <div id="albumPendingGrid"><div class="text-muted small">${this._esc(this.t('loading'))}</div></div>
                    </div>
                </div>
                <div class="card" id="albumLiveCard">
                    <div class="card-body">
                        <h6 class="mb-2">${this._esc(this.t('livePhotos'))}</h6>
                        <div id="albumLiveGrid"><div class="text-muted small">${this._esc(this.t('loading'))}</div></div>
                    </div>
                </div>
            </div>
        `;

        this._wire();
        if (a.enabled) {
            // REST load first so the tab paints even if rules/offline block the
            // listener, then attach the live feed on top of it.
            this._loadPhotos();
            this._startLiveFeed();
        } else {
            this._stopLiveFeed();
        }
    }

    // -----------------------------------------------------------------------
    // OFF state
    // -----------------------------------------------------------------------

    /**
     * What a host sees before they switch the album on.
     *
     * The old off state was the header card and nothing else — a switch, on an
     * empty page, for a feature most hosts have never seen anywhere. This says
     * what it is, walks the three steps, states the free terms plainly (read
     * from the server's LIMITS so the copy cannot drift from what the cron
     * does), and ends in the switch it is asking for. Deliberately no imagery:
     * a wall of sample photographs on a settings tab reads as an ad.
     *
     * Shown whenever the album is OFF, including for an album that already
     * holds photos — a switched-off album renders nothing else, so gating this
     * on the photo count just puts the blank page back for the host who most
     * needs telling that guests currently cannot add anything.
     */
    _promoCard(a) {
        const L = this.limits || {};
        const visible = a.freeVisible || L.FREE_VISIBLE || 30;
        const months = L.FREE_RETENTION_MONTHS || 12;
        const price = a.unlockPriceUSD || L.UNLOCK_PRICE_USD || 49;

        // Task #809: a card, not a text block. A benefit headline with one
        // line under it, three feature tiles (icon, a few words, one line),
        // the action and the example link together, and the terms last in a
        // muted caption - instead of a paragraph, a headline built on "you
        // will see about ten", and a row of run-on notes.
        const feature = (icon, title, text) => `
            <div class="col-md-4">
                <div class="album-promo-feature">
                    <span class="album-promo-icon"><i class="bi ${icon}"></i></span>
                    <div>
                        <div class="album-promo-feature-title">${this._esc(title)}</div>
                        <div class="album-promo-feature-text">${this._esc(text)}</div>
                    </div>
                </div>
            </div>`;

        return `
            <div class="card mb-3 album-promo">
                <div class="card-body">
                    <h5 class="album-promo-lead">${this._esc(this.t('promoLead'))}</h5>
                    <p class="album-promo-sub">${this._esc(this.t('promoBody'))}</p>
                    <div class="row g-3 mb-4">
                        ${feature('bi-qr-code', this.t('promoFeat1'), this.t('promoStep1'))}
                        ${feature('bi-phone', this.t('promoFeat2'), this.t('promoStep2'))}
                        ${feature('bi-images', this.t('promoFeat3'), this.t('promoStep3'))}
                    </div>
                    <div class="d-flex align-items-center gap-3 flex-wrap">
                        <button class="btn btn-primary" id="albumPromoCta" type="button">
                            <i class="bi bi-camera me-2"></i>${this._esc(this.t('promoCta'))}
                        </button>
                        <a id="albumPromoDemo" class="album-promo-demo" href="${this._esc(DashboardAlbum.DEMO_ALBUM_URL)}"
                           target="_blank" rel="noopener">${this._esc(this.t('promoDemoLink'))} <i class="bi bi-arrow-right"></i></a>
                    </div>
                    <div class="album-promo-terms">${this._esc(this.t('promoFree', { n: visible, months, price }))}</div>
                </div>
            </div>`;
    }

    /** Scoped styles — the tab's own look without touching the global sheet. */
    _styles() {
        return `<style>
            /* ===== Task #763: give this tab's cards their edges back =======
               MEASURED in Firefox against the real dashboard cascade: a .card
               dropped into this tab computes border 0px none, top radius 0px,
               shadow none, and its body 24px of padding on opaque white. The
               rule is assets/css/styles.css:3377 —

                 .tab-content .card { border:none; border-radius:0 0 8px 8px;
                                      box-shadow:none }
                 .tab-content .card-body { padding:24px; background:#fff }

               — written for the ONE card that IS a tab's body, sitting flush
               under the tab strip. This tab nests eight cards inside that
               pane, so each of them lost its edges and gained 24px of padding:
               a stack of full-bleed white bands with nothing grouping them.
               Scoped back HERE rather than changing that rule, which every
               other dashboard tab is built on. ================================ */
            #albumMainContent .card {
                border:1px solid rgba(16,24,40,.09);
                border-radius:14px;
                box-shadow:0 1px 2px rgba(16,24,40,.04);
            }
            /* styles.css lifts every .card on hover. On a settings tab that
               makes the whole page twitch under the pointer. */
            #albumMainContent .card:hover {
                transform:none;
                box-shadow:0 1px 2px rgba(16,24,40,.04);
            }
            #albumMainContent .card > .card-body {
                padding:18px 20px;
                background:transparent;
                border-radius:inherit;
            }
            [data-bs-theme="dark"] #albumMainContent .card,
            .dark-mode #albumMainContent .card { border-color:#39324d; }

            /* A quiet label over a group of fields, so they read as one block
               rather than three fields that happen to be near each other. */
            #albumMainContent .album-sec-title {
                font-size:.72rem; font-weight:600; letter-spacing:.07em;
                text-transform:uppercase; color:#8a8a93; margin:0 0 14px;
            }

            /* Three counters, one card, hairlines between. border-inline-start
               so the dividers stay between the cells on an RTL dashboard. */
            #albumMainContent .album-stats { display:flex; padding:6px 0; }
            #albumMainContent .album-stats > div { flex:1 1 0; text-align:center; padding:10px 8px; }
            #albumMainContent .album-stats > div + div { border-inline-start:1px solid rgba(16,24,40,.08); }
            [data-bs-theme="dark"] #albumMainContent .album-stats > div + div,
            .dark-mode #albumMainContent .album-stats > div + div { border-inline-start-color:#39324d; }
            #albumMainContent .album-stats .n { font-size:1.5rem; font-weight:600; line-height:1.15; }
            #albumMainContent .album-stats .l { font-size:.78rem; color:#6c757d; margin-top:2px; }

            /* The switch was Bootstrap's own blue (measured: rgb(13,110,253))
               on a dashboard whose primary is onyx — the only blue on the tab,
               next to a black Print button. */
            #albumMainContent .form-check-input:checked {
                background-color:var(--primary-700,#1C1C1C);
                border-color:var(--primary-700,#1C1C1C);
            }
            #albumMainContent .form-check-input:focus {
                border-color:rgba(28,28,28,.35);
                box-shadow:0 0 0 .2rem rgba(28,28,28,.12);
            }

            /* The tint goes on the .card-body, not the .card: styles.css paints
               .tab-content .card-body solid white, which covers any background
               set on the card itself and leaves only the border. */
            /* Task #763: these carry the id too, or the transparent .card-body
               above them wins on specificity and the tint disappears. */
            #albumMainContent .qr-hero, .qr-hero { border:1px solid #e6dcc8; }
            #albumMainContent .qr-hero > .card-body,
            .qr-hero > .card-body { background:linear-gradient(135deg,#faf8f3 0%,#f3ede1 100%); border-radius:inherit; }
            [data-bs-theme="dark"] #albumMainContent .qr-hero, .dark-mode #albumMainContent .qr-hero,
            [data-bs-theme="dark"] .qr-hero, .dark-mode .qr-hero { border-color:#39324d; }
            [data-bs-theme="dark"] #albumMainContent .qr-hero > .card-body, .dark-mode #albumMainContent .qr-hero > .card-body,
            [data-bs-theme="dark"] .qr-hero > .card-body, .dark-mode .qr-hero > .card-body { background:linear-gradient(135deg,#262233 0%,#1e1b2a 100%); }
            .qr-hero .qr-chip { width:104px; height:104px; border-radius:12px; background:#fff; padding:8px;
                box-shadow:0 2px 10px rgba(0,0,0,.12); flex:0 0 auto; }
            .qr-hero .qr-chip img { width:100%; height:100%; display:block; }
            /* Task #763: these two rows used to be spelled with Bootstrap
               utilities - d-flex gap-2 flex-wrap - and styles.css:5481 says,
               for every phone:

                 .card .d-flex.gap-2.flex-wrap { display:grid !important;
                                                 grid-template-columns:1fr 1fr }
                 .card .d-flex.gap-2.flex-wrap .btn { width:100% }

               written for ONE card's Dashboard/Delete row. The selector is
               three utility classes, so it matched this card by accident, and
               1fr columns cannot shrink below the longest label: measured at
               375px, "Print QR cards" drew 370px wide and ended at x=405
               against a tab edge at x=361. Named classes do not answer to it. */
            #albumMainContent .album-link-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
            #albumMainContent .album-btn-row { display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; }
            #albumMainContent .album-btn-row .btn { flex:0 1 auto; }
            @media (max-width: 575.98px) {
                #albumMainContent .qr-hero .qr-chip { width:88px; height:88px; }
                /* The content column takes a row of its own under the chip.
                   !important because the element carries an inline flex basis,
                   and an inline style beats any rule that does not say it. */
                #albumMainContent .qr-hero .card-body > .d-flex > div:last-child {
                    flex:1 1 100% !important; min-width:0;
                }
            }
            .album-locked-strip { display:flex; gap:6px; overflow:hidden; }
            .album-locked-strip .cell { position:relative; width:74px; height:74px; border-radius:8px; overflow:hidden; flex:0 0 auto; }
            .album-locked-strip .cell img { width:100%; height:100%; object-fit:cover; display:block; filter:blur(1.5px) saturate(.75); opacity:.85; }
            .album-locked-strip .cell::after { content:""; position:absolute; inset:0; background:rgba(20,16,30,.28); }
            .album-locked-strip .more { display:flex; align-items:center; justify-content:center; width:74px; height:74px;
                border-radius:8px; background:rgba(120,110,150,.14); font-size:.78rem; font-weight:600; flex:0 0 auto; }
            .album-photo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(120px,1fr)); gap:10px; }
            .album-jrows { display:flex; flex-direction:column; gap:10px; }
            .album-jrow { display:flex; gap:10px; }
            .album-jcell { flex:0 0 auto; }
            .album-jcell img { width:100%; object-fit:cover; border-radius:8px; display:block; }
            .album-photo-grid img { width:100%; aspect-ratio:1/1; object-fit:cover; border-radius:8px; display:block; }
            .album-empty { text-align:center; padding:26px 18px; }
            /* OFF-state pitch: an ordinary tinted card, no imagery. */
            .album-promo { border:1px solid #e6dcc8; overflow:hidden; }
            [data-bs-theme="dark"] .album-promo, .dark-mode .album-promo { border-color:#39324d; }
            #albumMainContent .album-promo > .card-body,
            .album-promo > .card-body { background:linear-gradient(135deg,#faf8f3 0%,#f3ede1 100%); }
            [data-bs-theme="dark"] #albumMainContent .album-promo > .card-body, .dark-mode #albumMainContent .album-promo > .card-body,
            [data-bs-theme="dark"] .album-promo > .card-body, .dark-mode .album-promo > .card-body {
                background:linear-gradient(135deg,#262233 0%,#1e1b2a 100%); }
            .album-promo-step { display:flex; gap:10px; align-items:flex-start; font-size:.92rem; line-height:1.4; }
            .album-promo-step i { flex:0 0 auto; opacity:.5; line-height:1.4; }
            /* Task #809 */
            .album-promo > .card-body { padding:1.6rem 1.6rem 1.3rem; }
            .album-promo-lead { font-size:1.25rem; font-weight:600; margin:0 0 .35rem; letter-spacing:-.01em; }
            .album-promo-sub { color:#6b6353; margin:0 0 1.3rem; font-size:.95rem; }
            .album-promo-feature { display:flex; gap:.75rem; align-items:flex-start; height:100%;
                background:rgba(255,255,255,.72); border:1px solid #ece3d2; border-radius:12px; padding:.85rem .9rem; }
            .album-promo-icon { flex:0 0 auto; width:34px; height:34px; border-radius:50%; background:#f3e8cf;
                color:#8a6d2f; display:inline-flex; align-items:center; justify-content:center; font-size:1rem; }
            .album-promo-feature-title { font-weight:600; font-size:.9rem; line-height:1.3; }
            .album-promo-feature-text { color:#6b6353; font-size:.84rem; line-height:1.4; margin-top:.15rem; }
            .album-promo-demo { color:#16243F; font-weight:500; text-decoration:none; }
            .album-promo-demo:hover { text-decoration:underline; }
            [dir="rtl"] .album-promo-demo i, html[dir="rtl"] .album-promo-demo i { display:inline-block; transform:scaleX(-1); }
            .album-promo-terms { margin-top:1.1rem; padding-top:.8rem; border-top:1px solid #ece3d2;
                color:#8c8577; font-size:.78rem; }
            [data-bs-theme="dark"] .album-promo-feature, .dark-mode .album-promo-feature { background:rgba(255,255,255,.04); border-color:#39324d; }
            [data-bs-theme="dark"] .album-promo-sub, [data-bs-theme="dark"] .album-promo-feature-text,
            .dark-mode .album-promo-sub, .dark-mode .album-promo-feature-text { color:#b7b0c2; }
            [data-bs-theme="dark"] .album-promo-demo, .dark-mode .album-promo-demo { color:#e8e2f3; }
        </style>`;
    }

    _shareCard(a) {
        return `
            <div class="card mb-3 qr-hero">
                <div class="card-body">
                    <div class="d-flex align-items-center gap-3 flex-wrap">
                        <div class="qr-chip"><img id="albumQrPreview" alt="" src=""></div>
                        <div style="flex:1 1 220px; min-width:0;">
                            <div class="fw-semibold mb-1">${this._esc(this.t('link'))}</div>
                            <div class="album-link-row">
                                <input type="text" class="form-control form-control-sm" id="albumLink" readonly
                                       value="${this._esc(this.albumUrl)}" style="flex:1 1 180px; min-width:0;">
                                <button class="btn btn-sm btn-outline-secondary" id="albumCopyBtn" type="button">${this._esc(this.t('copy'))}</button>
                            </div>
                            <div class="album-btn-row">
                                <button class="btn btn-primary btn-sm" id="albumQrCardBtn" type="button">
                                    <i class="bi bi-printer me-1"></i>${this._esc(this.t('qrCard'))}
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" id="albumQrBtn" type="button">
                                    <i class="bi bi-qr-code me-1"></i>${this._esc(this.t('qrImage'))}
                                </button>
                                <a class="btn btn-sm btn-outline-secondary" id="albumPreviewBtn"
                                   href="${this._esc(this.albumUrl)}" target="_blank" rel="noopener">
                                    <i class="bi bi-box-arrow-up-right me-1"></i>${this._esc(this.t('emptyPreview'))}
                                </a>
                                <button class="btn btn-sm btn-outline-secondary" id="albumAddOwnBtn" type="button"
                                        title="${this._esc(this.t('addOwnTip'))}">
                                    <i class="bi bi-cloud-arrow-up me-1"></i>${this._esc(this.t('addOwn'))}
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" id="albumSlideshowBtn" type="button"
                                        title="${this._esc(this.t('slideshowTip'))}">
                                    <i class="bi bi-display me-1"></i>${this._esc(this.t('slideshow'))}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    _settingsCard(a) {
        return `
            <div class="card mb-3">
                <div class="card-body">
                    <div class="album-sec-title">${this._esc(this.t('settings'))}</div>
                    <label class="form-label small text-muted" for="albumTitleInput">${this._esc(this.t('albumTitle'))}</label>
                    <input type="text" class="form-control mb-1" id="albumTitleInput" maxlength="120"
                           value="${this._esc(a.title || '')}">
                    <div class="form-text mb-3">${this._esc(this.t('albumTitleHint'))}</div>

                    <label class="form-label small text-muted" for="albumPrivacy">${this._esc(this.t('privacy'))}</label>
                    <select class="form-select mb-1" id="albumPrivacy">
                        <option value="public" ${a.privacy === 'public' ? 'selected' : ''}>${this._esc(this.t('privacyPublic'))}</option>
                        <option value="guests" ${a.privacy === 'guests' ? 'selected' : ''} ${a.formId ? '' : 'disabled'}>${this._esc(this.t('privacyGuests'))}</option>
                    </select>
                    ${a.formId ? '' : `<div class="form-text mb-3">${this._esc(this.t('privacyGuestsNoForm'))}</div>`}

                    <div class="form-check form-switch mt-3">
                        <input class="form-check-input" type="checkbox" role="switch" id="albumReview" ${a.reviewMode ? 'checked' : ''}>
                        <label class="form-check-label" for="albumReview">${this._esc(this.t('review'))}</label>
                    </div>
                    <div class="form-text">${this._esc(this.t('reviewHint'))}</div>

                    ${a.eventDate ? `
                    <div class="form-check form-switch mt-3">
                        <input class="form-check-input" type="checkbox" role="switch" id="albumOpensOnDay" ${a.opensOnEventDay ? 'checked' : ''}>
                        <label class="form-check-label" for="albumOpensOnDay">${this._esc(this.t('opensOnDay'))}</label>
                    </div>
                    <div class="form-text">${this._esc(this.t('opensOnDayHint', { date: this._eventDay(a.eventDate) }))}</div>` : ''}

                    <div class="form-text mt-3">
                        ${a.opensAt
                            ? this._esc(this.t('opensOnStatus', { date: this._date(a.opensAt) }))
                            : a.uploadsOpen
                            ? this._esc(this.t('uploadsClose', { date: this._date(a.uploadsCloseAt) }))
                            : this._esc(this.t('uploadsClosed'))}
                        &nbsp;·&nbsp;${this._esc(this.t('expires', { date: this._date(a.expiresAt) }))}
                    </div>
                    <div class="form-text text-success" id="albumSaveNote"></div>
                </div>
            </div>`;
    }

    /**
     * Task #763: ONE card with three cells and a hairline between them, not
     * three cards. As three, the dashboard's flattening rule gave them no
     * edges and 24px of padding each, so the numbers drifted apart across the
     * full width of the page with nothing tying them together.
     */
    _statsRow(a) {
        const cell = (key, n, label) => `
            <div>
                <div class="n" data-stat="${key}">${n}</div>
                <div class="l">${this._esc(label)}</div>
            </div>`;
        return `<div class="card mb-3" id="albumStatsRow">
            <div class="card-body album-stats">
                ${cell('live', a.counts.live, this.t('statLive'))}
                ${cell('hidden', a.counts.hidden || 0, this.t('statHidden'))}
                ${cell('queued', a.counts.queued, this.t('statQueued'))}
            </div>
        </div>`;
    }

    /**
     * The free tier's ACTUAL terms, stated on the card next to the offer.
     *
     * Without this the card only says what the host would gain, never what
     * they already have or what they stand to lose — so the first time they
     * learn photos are being held back is when photos start disappearing.
     * Saying it plainly up front is the difference between an upsell and a
     * hostage note, and it is the whole reason the free tier is generous.
     *
     * Every number is read from the server's LIMITS rather than typed into
     * copy, so the sentence cannot drift away from what the cron actually does.
     */
    _termsBlock(a) {
        const L = this.limits || {};
        const visible = a.freeVisible || L.FREE_VISIBLE || 30;
        const months = L.FREE_RETENTION_MONTHS || 12;
        const days = L.UPLOAD_WINDOW_DAYS || 90;
        const years = L.PAID_RETENTION_YEARS || 2;
        // Inline styles rather than utility classes: the dashboard's own list
        // CSS was overriding .d-flex here and dropping every icon onto its own
        // line above the text.
        const li = (icon, cls, text) =>
            `<li style="display:flex;align-items:flex-start;gap:8px;margin-bottom:6px;">
                <i class="bi ${icon} ${cls}" style="flex:0 0 auto;line-height:1.5;"></i>
                <span style="flex:1 1 auto;">${this._esc(text)}</span>
             </li>`;

        return `
            <div class="row g-3 mb-3 album-terms">
                <div class="col-sm-6">
                    <div class="fw-semibold small mb-2">${this._esc(this.t('termsNowTitle'))}</div>
                    <ul class="list-unstyled small text-muted mb-0">
                        ${li('bi-check2', 'text-secondary', this.t('termsFreeShown', { n: visible }))}
                        ${li('bi-check2', 'text-secondary', this.t('termsFreeKept', { months }))}
                        ${li('bi-exclamation-triangle', 'text-warning', this.t('termsFreeDeleted', { n: visible, days }))}
                    </ul>
                </div>
                <div class="col-sm-6">
                    <div class="fw-semibold small mb-2">${this._esc(this.t('termsPaidTitle'))}</div>
                    <ul class="list-unstyled small text-muted mb-0">
                        ${li('bi-check2', 'text-success', this.t('termsPaidAll'))}
                        ${li('bi-check2', 'text-success', this.t('termsPaidSize'))}
                        ${a.videoConfigured ? li('bi-check2', 'text-success', this.t('termsPaidVideo')) : ''}
                        ${li('bi-check2', 'text-success', this.t('termsPaidKept', { years }))}
                    </ul>
                </div>
            </div>`;
    }

    _unlockCard(a) {
        if (a.isPaid) {
            return `
                <div class="card mb-3 border-success">
                    <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <div><i class="bi bi-check-circle-fill text-success me-1"></i>${this._esc(this.t('unlocked'))}</div>
                            ${a.expiresAt ? `<div class="form-text mt-1">${this._esc(this.t('expires', { date: this._date(a.expiresAt) }))}</div>` : ''}
                        </div>
                        <div class="album-btn-row" style="margin-top:0;">
                            <button class="btn btn-outline-secondary" id="albumRenewBtn" type="button">
                                <i class="bi bi-arrow-repeat me-1"></i>${this._esc(this.t('renew', { price: (this.limits && this.limits.RENEWAL_PRICE_USD) || 10 }))}
                            </button>
                            <button class="btn btn-outline-success" id="albumDownloadBtn" type="button">
                                <i class="bi bi-download me-1"></i>${this._esc(this.t('download'))}
                            </button>
                        </div>
                    </div>
                </div>`;
        }
        // The album's own price first: one quoted to this host in writing is kept.
        const price = a.unlockPriceUSD || (this.limits && this.limits.UNLOCK_PRICE_USD) || 49;
        const hidden = a.counts.hidden || 0;
        // counts.live is EVERY live photo; `hidden` is the slice of it beyond
        // the free window, not an extra bucket on top. Adding them together
        // told a host with 36 photos they had 42.
        const body = hidden > 0
            ? this.t('unlockBody', { n: hidden, visible: a.freeVisible, total: a.counts.live })
            : this.t('unlockBodyNoHidden');
        return `
            <div class="card mb-3 ${hidden > 0 ? 'border-warning' : ''}">
                <div class="card-body">
                    <h6 class="mb-1">${this._esc(this.t('unlockTitle'))}</h6>
                    <div class="album-locked-strip mb-2" id="albumLockedStrip"></div>
                    <p class="text-muted mb-3" style="font-size:0.9rem;">${this._esc(body)}</p>
                    ${this._termsBlock(a)}
                    <button class="btn btn-primary" id="albumUnlockBtn" type="button">
                        ${this._esc(this.t('unlock', { price }))}
                    </button>
                    <div class="form-text mt-2">${this._esc(this.t('termsFootnote'))}</div>
                </div>
            </div>`;
    }

    // -----------------------------------------------------------------------
    // wiring
    // -----------------------------------------------------------------------

    _wire() {
        const on = (id, ev, fn) => document.getElementById(id)?.addEventListener(ev, fn);

        // Switching on is what MATERIALISES the album (see _ensureAlbum), so
        // this re-renders rather than un-hiding a wrap: until the save lands
        // there is no id and no slug to build the share card or QR from.
        on('albumEnabled', 'change', async (e) => {
            const el = e.target;
            el.disabled = true;
            try {
                await this._save({ enabled: el.checked });
            } finally {
                el.disabled = false;
            }
            this.render();
        });

        // The promo's own button — the switch is a long way up the page on a
        // phone, and the pitch has to end in the action it is pitching.
        on('albumPromoCta', 'click', async (e) => {
            const btn = e.currentTarget;
            btn.disabled = true;
            try {
                await this._save({ enabled: true });
            } finally {
                btn.disabled = false;
            }
            this.render();
        });

        on('albumPrivacy', 'change', (e) => this._save({ privacy: e.target.value }));
        on('albumReview', 'change', (e) => this._save({ reviewMode: e.target.checked }));
        // The offset travels with the choice, so the album opens at the host's
        // own midnight on the event day (see uploadsOpenAt on the server).
        on('albumOpensOnDay', 'change', async (e) => {
            await this._save({ opensOnEventDay: e.target.checked, tzOffsetMin: this._eventDayOffset() });
            this.render();
        });

        // Title saves on blur / Enter rather than per keystroke.
        const titleInput = document.getElementById('albumTitleInput');
        if (titleInput) {
            const commit = () => {
                const v = titleInput.value.trim();
                if (v && v !== this.album.title) this._save({ title: v });
            };
            titleInput.addEventListener('blur', commit);
            titleInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') titleInput.blur(); });
        }

        on('albumCopyBtn', 'click', async (e) => {
            // currentTarget is null once the handler awaits; read it first.
            const btn = e.currentTarget;
            try {
                await navigator.clipboard.writeText(this.albumUrl);
            } catch {
                const input = document.getElementById('albumLink');
                input.select(); document.execCommand('copy');
            }
            const original = btn.textContent;
            btn.textContent = this.t('copied');
            setTimeout(() => { btn.textContent = original; }, 1600);
        });

        on('albumQrBtn', 'click', () => this._downloadQr());
        on('albumAddOwnBtn', 'click', (e) => this._openOwnerUpload(e.currentTarget));
        on('albumSlideshowBtn', 'click', (e) => this._openOwnerUpload(e.currentTarget, 'slideshow'));
        on('albumQrCardBtn', 'click', () => this._openQrCard());
        on('albumUnlockBtn', 'click', (e) => this._unlock(e.currentTarget));
        on('albumRenewBtn', 'click', (e) => this._renew(e.currentTarget));
        on('albumDownloadBtn', 'click', (e) => this._download(e.currentTarget));

        if (this.album.enabled) this._loadQrPreview();
    }

    /**
     * Turn the DRAFT the tab was rendered from into a real album doc.
     *
     * GET /for deliberately does not write (see the service header — reading a
     * tab used to mint an album, and 30 of the first 36 albums were phantoms).
     * So the first setting a host changes materialises it, and every later save
     * is an ordinary PATCH.
     */
    /** Every write path in demo mode ends here — nothing is sent. */
    _demoRefuse() {
        const msg = 'Demo mode - changes are not saved';
        if (typeof window.showToast === 'function') window.showToast(msg, 'info');
        const note = document.getElementById('albumSaveNote');
        if (note) {
            note.className = 'form-text text-muted';
            note.textContent = msg;
            setTimeout(() => { if (note) note.textContent = ''; }, 2500);
        }
    }

    async _ensureAlbum() {
        if (this.album && this.album.id) return this.album;
        if (!this.formId) throw new Error(this.t('saveError'));
        const data = await this._api('/for?formId=' + encodeURIComponent(this.formId), { method: 'POST' });
        this.album = data.album;
        if (data.limits) this.limits = data.limits;
        return this.album;
    }

    async _save(patch) {
        const note = document.getElementById('albumSaveNote');
        if (this.isDemo) { this._demoRefuse(); this.render(); return; }
        try {
            await this._ensureAlbum();
            const data = await this._api(`/${this.album.id}/settings`, {
                method: 'PATCH', body: JSON.stringify(patch)
            });
            this.album = data.album;
            if (note) {
                note.className = 'form-text text-success';
                note.textContent = this.t('saved');
                setTimeout(() => { if (note) note.textContent = ''; }, 1500);
            }
        } catch (err) {
            if (note) {
                note.className = 'form-text text-danger';
                note.textContent = err.message || this.t('saveError');
            }
            this.render(); // put the controls back where the server actually is
        }
    }

    // -----------------------------------------------------------------------
    // QR: preview, raw image, and the printable card
    // -----------------------------------------------------------------------

    /** The QR endpoint is auth'd, so it can't be a plain <img src>. */
    async _qrDataUrl() {
        if (this._qrCache) return this._qrCache;
        if (this.isDemo) {
            // The QR endpoint is auth'd, and the demo has no token. The card is
            // the point of the tab, so draw it here instead of hiding it —
            // qrcode-generator is already on the dashboard for check-in.
            const qr = window.qrcode(0, 'M');
            qr.addData(this.albumUrl);
            qr.make();
            this._qrCache = qr.createDataURL(8, 0);
            return this._qrCache;
        }
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`/api/albums/${this.album.id}/qr`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('qr failed');
        const blob = await res.blob();
        this._qrCache = await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
        });
        return this._qrCache;
    }

    async _loadQrPreview() {
        try {
            const img = document.getElementById('albumQrPreview');
            if (img) img.src = await this._qrDataUrl();
        } catch { /* preview is decorative — never block the tab on it */ }
    }

    /**
     * "Add your own photos": the album page with a 7-day owner token, so the
     * host uploads without the 25-per-guest limit (and past a guests-only
     * name check). The tab is opened before the request so a popup blocker
     * treats it as the click's own window.
     */
    // 'slideshow' opens the reception slideshow instead, with the same token:
    // a guests-only album shows on the screen only to it.
    async _openOwnerUpload(btn, target) {
        // The demo has no signed-in host to mint a token for. Its album is
        // public, so the slideshow opens as it is; an upload is refused like
        // every other demo write, instead of a blank tab and 'Not signed in'.
        if (this.isDemo) {
            if (target !== 'slideshow') return this._demoRefuse();
            window.open(`/album/${DashboardAlbum.DEMO_ALBUM_SLUG}/slideshow?lang=${encodeURIComponent(this.lang || 'en')}`, '_blank', 'noopener');
            return;
        }
        const win = window.open('about:blank', '_blank');
        if (btn) btn.disabled = true;
        try {
            const data = await this._api(`/${encodeURIComponent(this.album.id)}/owner-link`, { method: 'POST' });
            const url = target === 'slideshow'
                ? `${location.origin}${data.slideshowPath}&lang=${encodeURIComponent(this.lang || 'en')}`
                : location.origin + data.path;
            if (win) win.location.href = url; else location.href = url;
        } catch (err) {
            if (win) win.close();
            window.alert(err.message || this.t('saveError'));
        } finally {
            if (btn) btn.disabled = false;
        }
    }

    async _downloadQr() {
        try {
            const dataUrl = await this._qrDataUrl();
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `album-qr-${this.album.slug}.png`;
            document.body.appendChild(a); a.click(); a.remove();
        } catch (err) { window.alert(err.message || this.t('saveError')); }
    }

    /**
     * The printable table card.
     *
     * This is the artefact the whole feature depends on: it sits on a table at
     * a wedding, so it has to look like it belongs there, not like a dashboard
     * export. Opened in its own window with self-contained print CSS, laid out
     * as tiles on A4 so one sheet yields several table cards. The URL is
     * printed under the QR as a fallback for anyone whose camera won't scan.
     */
    async _openQrCard() {
        let qr;
        try { qr = await this._qrDataUrl(); }
        catch (err) { window.alert(err.message || this.t('saveError')); return; }

        const win = window.open('', '_blank');
        if (!win) { window.alert('Please allow pop-ups to print your QR cards.'); return; }

        const esc = (s) => this._esc(s);
        const dir = this.isRtl ? 'rtl' : 'ltr';
        const card = `
            <div class="card">
              <div class="rule"></div>
              <div class="names">${esc(this.album.title || '')}</div>
              <div class="heading">${esc(this.t('cardHeading'))}</div>
              <img class="qr" src="${qr}" alt="">
              <div class="sub">${esc(this.t('cardSub'))}</div>
              <div class="url">${esc(this.albumUrlPretty)}</div>
              <div class="foot">${esc(this.t('cardFooter'))} &middot; QuikRSVP</div>
              <div class="rule"></div>
            </div>`;

        win.document.write(`<!DOCTYPE html><html lang="${esc(this.lang)}" dir="${dir}"><head>
<meta charset="utf-8"><title>${esc(this.t('qrCard'))}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600&family=Inter:wght@400;500&display=swap" rel="stylesheet">
<style>
  *,*::before,*::after{box-sizing:border-box}
  body{margin:0;background:#e9e6df;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#2a2419}
  .bar{position:sticky;top:0;display:flex;gap:12px;align-items:center;justify-content:center;
       padding:12px;background:#fff;border-bottom:1px solid #d9d2c4;z-index:5}
  .bar button{font:inherit;font-weight:600;padding:9px 20px;border:0;border-radius:999px;
       background:#1c1c1c;color:#fff;cursor:pointer}
  .bar label{font-size:.9rem;color:#5c5344}
  .bar select{font:inherit;padding:7px 10px;border-radius:8px;border:1px solid #d9d2c4;background:#fff}
  .sheet{display:grid;gap:10mm;padding:12mm;justify-content:center}
  .sheet.n1{grid-template-columns:1fr}
  .sheet.n2{grid-template-columns:1fr 1fr}
  .sheet.n4{grid-template-columns:1fr 1fr}
  .card{background:#fffdf8;border:1px solid #ddd2ba;border-radius:14px;padding:22px 20px;text-align:center;
        box-shadow:0 1px 3px rgba(0,0,0,.06);break-inside:avoid}
  .rule{height:1px;background:linear-gradient(to right,transparent,#c9a227,transparent);margin:2px 0 14px}
  .card .rule:last-child{margin:14px 0 2px}
  .names{font-family:"Cormorant Garamond",Georgia,serif;font-size:1.5rem;font-weight:600;line-height:1.2;margin-bottom:2px}
  .heading{font-family:"Cormorant Garamond",Georgia,serif;font-size:1.15rem;color:#6b5f47;margin-bottom:14px;font-style:italic}
  .qr{width:150px;height:150px;display:block;margin:0 auto 12px}
  .sub{font-size:.86rem;color:#5c5344;margin-bottom:6px}
  .url{font-size:.8rem;letter-spacing:.02em;color:#8a7c62;word-break:break-all}
  .foot{font-size:.68rem;color:#a2957c;margin-top:12px;letter-spacing:.06em;text-transform:uppercase}
  .sheet.n1 .card{padding:44px 34px}
  .sheet.n1 .names{font-size:2.4rem}.sheet.n1 .heading{font-size:1.6rem}.sheet.n1 .qr{width:260px;height:260px}
  @media print{
    body{background:#fff}
    .bar{display:none}
    .sheet{padding:8mm;gap:8mm}
    @page{size:A4;margin:10mm}
  }
</style></head><body>
<div class="bar">
  <label for="n">${esc(this.t('cardCopies'))}</label>
  <select id="n"><option value="4">4</option><option value="2">2</option><option value="1">1</option></select>
  <button onclick="window.print()">${esc(this.t('cardPrint'))}</button>
</div>
<div class="sheet n4" id="sheet">${card.repeat(4)}</div>
<script>
  var CARD = ${JSON.stringify(card)};
  document.getElementById('n').addEventListener('change', function(){
    var n = parseInt(this.value, 10) || 4;
    var s = document.getElementById('sheet');
    s.className = 'sheet n' + n;
    s.innerHTML = new Array(n + 1).join(CARD);
  });
<\/script>
</body></html>`);
        win.document.close();
    }

    // -----------------------------------------------------------------------
    // money
    // -----------------------------------------------------------------------

    async _unlock(btn) {
        if (this.isDemo) return this._demoRefuse();
        btn.disabled = true;
        try {
            const data = await this._api(`/${this.album.id}/checkout`, {
                method: 'POST', body: JSON.stringify({ origin: location.origin })
            });
            if (data.url) location.href = data.url;
        } catch (err) {
            btn.disabled = false;
            window.alert(err.message || this.t('saveError'));
        }
    }

    /** One more year on a paid album ($10) — albums.service createRenewalCheckout. */
    async _renew(btn) {
        if (this.isDemo) return this._demoRefuse();
        btn.disabled = true;
        try {
            const data = await this._api(`/${this.album.id}/renew-checkout`, {
                method: 'POST', body: JSON.stringify({ origin: location.origin })
            });
            if (data.url) location.href = data.url;
        } catch (err) {
            btn.disabled = false;
            window.alert(err.message || this.t('saveError'));
        }
    }

    async _download(btn) {
        if (this.isDemo) return this._demoRefuse();
        const original = btn.innerHTML;
        btn.disabled = true;
        btn.textContent = this.t('preparing');
        try {
            const data = await this._api(`/${this.album.id}/download`);
            if (data.url) location.href = data.url;
        } catch (err) {
            window.alert(err.message || this.t('saveError'));
        } finally {
            btn.disabled = false;
            btn.innerHTML = original;
        }
    }

    // -----------------------------------------------------------------------
    // photos
    // -----------------------------------------------------------------------

    async _loadPhotos() {
        if (this.isDemo) return; // demo fixtures are painted by _initDemo
        const pendingEl = document.getElementById('albumPendingGrid');
        const liveEl = document.getElementById('albumLiveGrid');
        try {
            const [queued, flagged, live] = await Promise.all([
                this._api(`/${this.album.id}/photos?status=queued`),
                this._api(`/${this.album.id}/photos?status=flagged`),
                this._api(`/${this.album.id}/photos?status=live&limit=200`)
            ]);
            const pending = [...(flagged.photos || []), ...(queued.photos || [])];
            this.livePhotos = live.photos || [];

            if (pendingEl) {
                pendingEl.innerHTML = pending.length
                    ? this._photoGrid(pending, true)
                    : `<div class="text-muted small">${this._esc(this.t('noPending'))}</div>`;
            }
            this._renderApproveAll(queued.photos || []);

            this._renderLiveGrid(this.livePhotos);

            this._renderLockedStrip();
            this._renderEmptyState(pending.length);
            this._wirePhotoActions();
        } catch (err) {
            if (pendingEl) pendingEl.innerHTML = `<div class="text-danger small">${this._esc(err.message)}</div>`;
            if (liveEl) liveEl.innerHTML = '';
        }
    }

    _renderApproveAll(queued) {
        const slot = document.getElementById('albumApproveAllSlot');
        if (!slot) return;
        if (!queued.length) { slot.innerHTML = ''; return; }
        slot.innerHTML = `<button class="btn btn-sm btn-success" id="albumApproveAllBtn" type="button">
            <i class="bi bi-check2-all me-1"></i>${this._esc(this.t('approveAll', { n: queued.length }))}
        </button>`;
        document.getElementById('albumApproveAllBtn')?.addEventListener('click', async (e) => {
            if (!window.confirm(this.t('approveAllConfirm', { n: queued.length }))) return;
            const btn = e.currentTarget;
            btn.disabled = true;
            try {
                // includeFlagged stays false: the fast path must not wave through
                // the items screening pulled out for a human to look at.
                await this._api(`/${this.album.id}/photos/approve-all`, {
                    method: 'POST', body: JSON.stringify({ includeFlagged: false })
                });
                await this.refresh();
                this.render();
            } catch (err) {
                btn.disabled = false;
                window.alert(err.message || this.t('saveError'));
            }
        });
    }

    /**
     * The upsell strip: the actual photos being withheld, blurred. A count is
     * abstract; six faces the host recognises are not.
     */
    _renderLockedStrip() {
        const strip = document.getElementById('albumLockedStrip');
        if (!strip) return;
        const hidden = this.album.counts.hidden || 0;
        if (this.album.isPaid || hidden <= 0 || !this.livePhotos.length) {
            strip.innerHTML = '';
            return;
        }
        const sample = this.livePhotos.slice(this.album.freeVisible, this.album.freeVisible + 6);
        if (!sample.length) { strip.innerHTML = ''; return; }
        const rest = hidden - sample.length;
        strip.innerHTML = sample.map((p) => `
            <div class="cell"><img src="${this._esc(p.thumbUrl)}" alt="" loading="lazy"></div>
        `).join('') + (rest > 0
            ? `<div class="more">${this._esc(this.t('andMore', { n: rest }))}</div>` : '');
    }

    /** Before any guest has uploaded, point at the card instead of saying "none". */
    _renderEmptyState(pendingCount) {
        const slot = document.getElementById('albumEmptySlot');
        if (!slot) return;
        const total = (this.album.counts.total || 0) + pendingCount;
        if (total > 0) {
            slot.innerHTML = '';
            // Task #763: and put back what the empty state hid — the live feed
            // calls this again the moment the first photo lands.
            const back = document.getElementById('albumLiveCard');
            if (back) back.style.display = '';
            return;
        }
        slot.innerHTML = `
            <div class="card mb-3">
                <div class="card-body album-empty">
                    <i class="bi bi-camera" style="font-size:1.8rem;opacity:.45"></i>
                    <h6 class="mt-2 mb-1">${this._esc(this.t('emptyTitle'))}</h6>
                    <p class="text-muted mb-3" style="font-size:.9rem;max-width:420px;margin-inline:auto;">
                        ${this._esc(this.t('emptyBody'))}</p>
                    <button class="btn btn-primary btn-sm" type="button" id="albumEmptyCardBtn">
                        <i class="bi bi-printer me-1"></i>${this._esc(this.t('qrCard'))}
                    </button>
                </div>
            </div>`;
        document.getElementById('albumEmptyCardBtn')?.addEventListener('click', () => this._openQrCard());
        // Nothing to moderate and nothing in the album — hide the two empty
        // panels. Task #763: the LIVE card was never one of them, so an empty
        // album said so twice: the card above ("Your album is ready and
        // waiting"), then "In the album / No guest photos yet." under it.
        const pendingCard = document.getElementById('albumPendingCard');
        if (pendingCard && pendingCount === 0) pendingCard.style.display = 'none';
        const liveCard = document.getElementById('albumLiveCard');
        if (liveCard) liveCard.style.display = 'none';
    }

    /**
     * Row packing for the justified gallery — the same greedy algorithm the
     * guest page uses (assets/js/album.js). Duplicated rather than shared
     * because that file is a standalone IIFE the dashboard never loads; if a
     * third caller ever appears, lift it into a shared util.
     */
    static packRows(photos, containerWidth, target, gap) {
        const clamp = (a) => Math.min(3.5, Math.max(0.4, a));
        const aspectOf = (p) => {
            if (!p.width || !p.height) return 1;
            const a = p.width / p.height;
            return (!isFinite(a) || a <= 0) ? 1 : clamp(a);
        };
        const rows = [];
        let row = [];
        let sum = 0;
        for (const p of photos) {
            const a = aspectOf(p);
            row.push({ photo: p, aspect: a });
            sum += a;
            if (sum * target + gap * (row.length - 1) >= containerWidth) {
                const avail = containerWidth - gap * (row.length - 1);
                rows.push({ items: row, height: avail / sum });
                row = []; sum = 0;
            }
        }
        if (row.length) {
            const avail = containerWidth - gap * (row.length - 1);
            // Trailing row keeps the target height rather than blowing one
            // leftover photo up to the full width.
            rows.push({ items: row, height: Math.min(target, avail / sum) });
        }
        return rows;
    }

    /**
     * The album view, justified so photos keep their shape. The REVIEW queue
     * above it stays square on purpose — uniform tiles are easier to scan when
     * you are approving a run of them, and shape matters less than rhythm there.
     */
    _renderLiveGrid(photos) {
        const el = document.getElementById('albumLiveGrid');
        if (!el) return;
        this._livePhotos = photos || [];
        if (!this._livePhotos.length) {
            el.innerHTML = `<div class="text-muted small">${this._esc(this.t('noPhotos'))}</div>`;
            return;
        }
        el.innerHTML = '<div class="album-jrows"></div>' + ((this.album && this.album.processing > 0)
            ? `<div class="form-text mt-2">${this._esc(this.t('videosProcessing', { n: this.album.processing }))}</div>` : '');
        this._layoutLiveGrid();
        if (typeof ResizeObserver !== 'undefined' && !this._liveObserver) {
            this._liveObserver = new ResizeObserver(() => {
                clearTimeout(this._liveLayoutTimer);
                this._liveLayoutTimer = setTimeout(() => this._layoutLiveGrid(), 120);
            });
            this._liveObserver.observe(el);
        }
    }

    _layoutLiveGrid() {
        const host = document.querySelector('#albumLiveGrid .album-jrows');
        if (!host || !this._livePhotos || !this._livePhotos.length) return;
        const width = host.clientWidth;
        // Zero while the tab is hidden — the ResizeObserver fires again when it
        // is shown, so leaving it empty for now is correct rather than lossy.
        if (!width) return;

        const gap = 10;
        const target = width < 500 ? 120 : 170;
        const rows = DashboardAlbum.packRows(this._livePhotos, width, target, gap);

        host.innerHTML = rows.map((row) => `
            <div class="album-jrow">
                ${row.items.map((item) => {
                    const p = item.photo;
                    const w = Math.floor(item.aspect * row.height);
                    const h = Math.floor(row.height);
                    return `
                    <div class="album-jcell" style="width:${w}px;">
                        <a href="${this._esc(this._mediaHref(p))}" target="_blank" rel="noopener" style="position:relative;display:block;">
                            <img src="${this._esc(p.thumbUrl)}" alt="" loading="lazy" style="height:${h}px;">
                            ${this._videoBadge(p)}
                        </a>
                        <div class="small text-muted text-truncate mt-1">${this._esc(p.uploaderName || '')}</div>
                        ${p.type === 'video' ? `<button class="btn btn-sm btn-outline-secondary w-100 mt-1 album-video-dl" data-id="${this._esc(p.id)}"><i class="bi bi-download me-1"></i>${this._esc(this.t('downloadVideo'))}</button>` : ''}
                        <button class="btn btn-sm btn-outline-danger w-100 mt-1 album-reject" data-id="${this._esc(p.id)}">${this._esc(this.t('reject'))}</button>
                    </div>`;
                }).join('')}
            </div>`).join('');
        this._wirePhotoActions();
    }

    _photoGrid(photos, showApprove) {
        const cells = photos.map((p) => `
            <div style="position:relative;">
                <a href="${this._esc(this._mediaHref(p))}" target="_blank" rel="noopener" style="position:relative;display:block;">
                    <img src="${this._esc(p.thumbUrl)}" alt="" loading="lazy">
                    ${this._videoBadge(p)}
                </a>
                ${p.status === 'flagged' ? `<span class="badge bg-warning text-dark" style="position:absolute;top:6px;inset-inline-start:6px;">${this._esc(this.t('flagged'))}</span>` : ''}
                <div class="small text-muted text-truncate mt-1">${this._esc(p.uploaderName || '')}</div>
                <div class="d-flex gap-1 mt-1">
                    ${showApprove ? `<button class="btn btn-sm btn-success flex-fill album-approve" data-id="${this._esc(p.id)}">${this._esc(this.t('approve'))}</button>` : ''}
                    <button class="btn btn-sm btn-outline-danger ${showApprove ? '' : 'flex-fill'} album-reject" data-id="${this._esc(p.id)}">${this._esc(this.t('reject'))}</button>
                </div>
            </div>
        `).join('');
        return `<div class="album-photo-grid">${cells}</div>`;
    }

    /** A video opens in Stream's own player; a photo in its display rendition. */
    _mediaHref(p) {
        return (p.type === 'video' && p.iframeUrl) ? p.iframeUrl : p.displayUrl;
    }

    _videoBadge(p) {
        if (p.type !== 'video') return '';
        const s = Math.round(p.durationSec || 0);
        const len = s ? ` ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '';
        return `<span class="badge bg-dark" style="position:absolute;top:6px;inset-inline-end:6px;opacity:.85;">&#9654;${len}</span>`;
    }

    async _downloadVideo(photoId, btn) {
        if (this.isDemo) return this._demoRefuse();
        btn.disabled = true;
        try {
            const d = await this._api(`/${this.album.id}/videos/${encodeURIComponent(photoId)}/download`);
            if (d.url && d.status === 'ready') location.href = d.url;
            else window.alert(this.t('videoPreparing'));
        } catch (err) {
            window.alert(err.message || this.t('saveError'));
        } finally {
            btn.disabled = false;
        }
    }

    _wirePhotoActions() {
        document.querySelectorAll('.album-video-dl').forEach((btn) => {
            if (btn.dataset.wired) return;
            btn.dataset.wired = '1';
            btn.addEventListener('click', () => this._downloadVideo(btn.dataset.id, btn));
        });
        // _renderLiveGrid() wires, then its callers wire again: without the
        // guard every pending button got two listeners — two POSTs per click
        // and, for Reject, a second confirm and a "Photo not found" alert.
        document.querySelectorAll('.album-approve').forEach((btn) => {
            if (btn.dataset.wired) return;
            btn.dataset.wired = '1';
            btn.addEventListener('click', () => this._moderate(btn.dataset.id, 'approve', btn));
        });
        document.querySelectorAll('.album-reject').forEach((btn) => {
            if (btn.dataset.wired) return;
            btn.dataset.wired = '1';
            btn.addEventListener('click', () => {
                if (!window.confirm(this.t('rejectConfirm'))) return;
                this._moderate(btn.dataset.id, 'reject', btn);
            });
        });
    }

    async _moderate(photoId, action, btn) {
        if (this.isDemo) return this._demoRefuse();
        btn.disabled = true;
        try {
            await this._api(`/${this.album.id}/photos/${encodeURIComponent(photoId)}/moderate`, {
                method: 'POST', body: JSON.stringify({ action })
            });
            await this.refresh();
            this.render();
        } catch (err) {
            btn.disabled = false;
            window.alert(err.message || this.t('saveError'));
        }
    }

    showNoFormError() {
        const loading = document.getElementById('albumLoadingState');
        if (loading) loading.innerHTML = `<p class="text-muted mb-0">${this._esc(this.t('loadError'))}</p>`;
    }
}

window.dashboardAlbum = new DashboardAlbum();

document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('album-tab')?.addEventListener('shown.bs.tab', () => {
        const formId = typeof getCurrentFormId === 'function' ? getCurrentFormId() : null;
        if (formId) window.dashboardAlbum.init(formId);
        else window.dashboardAlbum.showNoFormError();
    });

    document.getElementById('form-select')?.addEventListener('change', (e) => {
        if (document.getElementById('album-tab')?.classList.contains('active')) {
            window.dashboardAlbum.init(e.target.value);
        }
    });
});
